import React, { useCallback, useEffect, useRef, useState } from 'react';
import { allowedReservationStatuses, FO_RES_RECENT_KEY } from '../utils/frontOfficeMenus';
import useMobileValidation from '../hooks/useMobileValidation';
import useIsMobile from '../hooks/useIsMobile';
import PmsComplianceCheckin from './PmsComplianceCheckin';
import GuestIntelligenceCard from '../components/GuestIntelligenceCard';
import { fmtDateMedium, dateTimeLocalToIso } from '../utils/dateUtils';
import LockEncodeModal from '../components/LockEncodeModal';
import ArrivalsImportModal from '../components/ArrivalsImportModal';

const toIsoDate = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};
const toDateTimeLocalValue = (value) => {
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return '';
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  const hh = String(dt.getHours()).padStart(2, '0');
  const mi = String(dt.getMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
};
const addDaysIso = (dateText, days) => {
  if (!dateText) return '';
  const [y, m, d] = String(dateText).split('-').map(Number);
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return '';
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return '';
  dt.setDate(dt.getDate() + Number(days || 0));
  return toIsoDate(dt);
};
const toDateInputValue = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw.length >= 10 ? raw.slice(0, 10) : raw;
};
const normalizeCountry = (value) => String(value || '').trim().toLowerCase();
const buildDefaultComplianceData = (hotelCountry, guestName = '') => ({
  guest_name: guestName,
  nationality: hotelCountry || 'India',
  is_foreign_guest: false
});

const buildGuestList = (hotelCountry, primaryName, totalAdults, eachGuest) => {
  const count = (eachGuest && totalAdults > 1) ? totalAdults : 1;
  return Array.from({ length: count }, (_, i) =>
    buildDefaultComplianceData(hotelCountry, i === 0 ? primaryName : '')
  );
};

const emptyLine = {
  room_type_id: '',
  room_id: '',
  arrival_date: '',
  departure_date: '',
  adults: 1,
  children: 0,
  rate_plan_code: '',
  rate_amount: 0,
  tax_amount: 0
};

const nightsBetween = (arrival, departure) => {
  if (!arrival || !departure) return 0;
  const a = new Date(`${arrival}T00:00:00`);
  const d = new Date(`${departure}T00:00:00`);
  const diff = d - a;
  return diff > 0 ? Math.round(diff / 86400000) : 0;
};

const buildEmptyForm = () => {
  const arrival = toIsoDate(new Date());
  const departure = addDaysIso(arrival, 1);
  return {
    booking_source: 'DIRECT',
    status: 'TENTATIVE',
    corporate_id: '',
    travel_agent_id: '',
    travel_agent_commission_percent: 0,
    travel_agent_commission_amount: 0,
    bill_to: 'SELF',
    employee_id: '',
    vessel_name: '',
    care_of: '',
    sales_member_id: '',
    credit_hold_amount: '',
    arrival_date: arrival,
    departure_date: departure,
    primary_guest_name: '',
    mobile_number: '',
    email: '',
    gst_number: '',
    company_name: '',
    billing_address: '',
    room_count: 1,
    total_pax: 1,
    advance_amount: '',
    advance_payment_mode: 'CASH',
    advance_reference_no: '',
    advance_remarks: '',
    lines: [{ ...emptyLine, arrival_date: arrival, departure_date: departure }]
  };
};

function PmsReservation({ token, role, permissions, foMenuAccess = null, selectedProperty, initialEditId, onInitialEditConsumed, prefillGuest, onPrefillGuestConsumed, aiGuestRecognitionEnabled }) {
  // Users → Front Office Menus → Reservation Screen (limited users only).
  const allowedStatuses = allowedReservationStatuses(foMenuAccess);
  const showRecentReservations = !Array.isArray(foMenuAccess) || foMenuAccess.includes(FO_RES_RECENT_KEY);
  const statusOptions = (current) => ['TENTATIVE', 'CONFIRMED', 'GUARANTEED']
    .filter((st) => allowedStatuses.includes(st) || st === current)
    .map((st) => <option key={st} value={st}>{st}</option>);
  const { isMobile } = useIsMobile();
  const { validateMobile } = useMobileValidation(token, selectedProperty);
  const [rows, setRows] = useState([]);
  // Recent Reservations filters — sent to the server on Search (loadData) and
  // also applied live to the loaded rows while typing.
  const [resFrom, setResFrom] = useState('');
  const [resTo, setResTo] = useState('');
  const [resSearch, setResSearch] = useState('');
  const [resDateBasis, setResDateBasis] = useState('arrival'); // 'arrival' | 'booking'
  // Server-side paging for Recent Reservations (RES_PAGE_SIZE per page).
  const RES_PAGE_SIZE = 50;
  const [resPage, setResPage] = useState(0);
  const [resTotal, setResTotal] = useState(0);
  const resPageCount = Math.max(1, Math.ceil(resTotal / RES_PAGE_SIZE));
  const reservationFilterActive = Boolean(resFrom || resTo || resSearch.trim());
  // Filter by arrival OR booking date (resDateBasis) + Res No / Guest search,
  // applied live to the page the server returned.
  const displayReservations = (() => {
    const q = resSearch.trim().toLowerCase();
    let list = rows.filter((r) => {
      const dateVal = resDateBasis === 'booking' ? (r.booking_date || '') : (r.arrival_date || '');
      if (resFrom && (!dateVal || dateVal < resFrom)) return false;
      if (resTo && (!dateVal || dateVal > resTo)) return false;
      if (q) {
        const no = String(r.reservation_no || `RES-${r.reservation_id}`).toLowerCase();
        const nm = String(r.primary_guest_name || '').toLowerCase();
        if (!no.includes(q) && !nm.includes(q)) return false;
      }
      return true;
    });
    list = [...list].sort((a, b) => Number(b.reservation_id || 0) - Number(a.reservation_id || 0));
    return list;
  })();
  const [types, setTypes] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [corporates, setCorporates] = useState([]);
  const [travelAgents, setTravelAgents] = useState([]);
  const [salesTeam, setSalesTeam] = useState([]);
  const [adhocAgentName, setAdhocAgentName] = useState('');
  const [adhocAgentCommission, setAdhocAgentCommission] = useState('');
  const [roomTaxPolicyConfigured, setRoomTaxPolicyConfigured] = useState(false);
  const [form, setForm] = useState(buildEmptyForm);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [showArrivalsImport, setShowArrivalsImport] = useState(false);
  const [statusDraft, setStatusDraft] = useState({});
  const [editingId, setEditingId] = useState('');
  // A new booking starts as TENTATIVE; move it to the first status this user
  // may set when TENTATIVE isn't one of them (the server would refuse it).
  useEffect(() => {
    if (!editingId && !allowedStatuses.includes(form.status)) setForm((f) => ({ ...f, status: allowedStatuses[0] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId, form.status, allowedStatuses.join()]);
  // Bug 8662 — ref + flag to scroll the edit form into view and briefly
  // highlight it when a reservation is opened for editing from the rack.
  const editFormRef = useRef(null);
  const [editHighlight, setEditHighlight] = useState(false);
  const [editingOtaInfo, setEditingOtaInfo] = useState(null); // OTA/CM info for current edit
  const [ciModal, setCiModal] = useState(null);
  const [ciForm, setCiForm] = useState({ checkin_datetime: '', checkout_datetime: '', deposit_amount: '', deposit_payment_mode: 'CASH', deposit_reference_no: '', deposit_remarks: '', room_id: '' });
  const [ciBookingAdvance, setCiBookingAdvance] = useState(0); // advance already collected at booking (info only)
  const [ciLines, setCiLines] = useState([]); // all room lines for group check-in
  const [ciNeedsRoom, setCiNeedsRoom] = useState(false);  // true when CM booking has no room assigned
  const [ciBusy, setCiBusy] = useState(false);
  const [ciError, setCiError] = useState('');
  const [ciResult, setCiResult] = useState(null);
  // Lock-encode modal opens after a successful reservation check-in. The
  // modal handles offline fallback on its own.
  const [lockEncodeModal, setLockEncodeModal] = useState(null);

  // Advance / Refund state
  const [advanceModal, setAdvanceModal] = useState(null); // reservation row
  const [editingAdvanceSummary, setEditingAdvanceSummary] = useState(null); // { net_advance, transactions[] } when editing
  const [advancesVoided, setAdvancesVoided] = useState([]); // voided advances for the Advance modal
  // Blocks a second Create/Update click while the first save is in flight —
  // double clicks were posting the booking advance two or three times.
  const [savingReservation, setSavingReservation] = useState(false);
  const savingReservationRef = useRef(false);
  const [advances, setAdvances] = useState([]);
  const [advanceForm, setAdvanceForm] = useState({ amount: '', payment_mode: 'CASH', reference_no: '', remarks: '' });
  const [advanceBusy, setAdvanceBusy] = useState(false);
  const [advanceError, setAdvanceError] = useState('');
  const [refundForm, setRefundForm] = useState({ amount: '', payment_mode: 'CASH', reference_no: '', remarks: '' });
  const [showRefundPanel, setShowRefundPanel] = useState(false);
  const [correctAdvanceRow, setCorrectAdvanceRow] = useState(null); // advance row being corrected
  const [correctForm, setCorrectForm] = useState({ new_mode: '', reference_no: '', reason: '' });
  const [correctBusy, setCorrectBusy] = useState(false);
  const [correctError, setCorrectError] = useState('');

  // Cancellation Modal State
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelPreview, setCancelPreview] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelForm, setCancelForm] = useState({ reason: '', overridePenalty: false });
  const [cancelError, setCancelError] = useState('');
  const [cancelAdvanceNet, setCancelAdvanceNet] = useState(0);

  // Compliance step state
  const [step, setStep] = useState(1);
  const [complianceGuestList, setComplianceGuestList] = useState([buildDefaultComplianceData(selectedProperty?.country)]);
  const [activeGuestIdx, setActiveGuestIdx] = useState(0);
  const [fieldRules, setFieldRules] = useState([]);
  const [policyConfig, setPolicyConfig] = useState({});
  const [countryOptions, setCountryOptions] = useState([]);
  const [idTypeOptions, setIdTypeOptions] = useState([]);
  const [complianceErrorsList, setComplianceErrorsList] = useState([{}]);
  const [overrideMode, setOverrideMode] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');

  // Inventory restriction modal state
  const [restrictionModal, setRestrictionModal] = useState(null); // { restrictions: [], payload: {}, url: '', method: '' }
  const [restrictionBusy, setRestrictionBusy] = useState(false);

  // Pre-check-in prompt after reservation creation
  const [preCheckinPrompt, setPreCheckinPrompt] = useState(null); // { reservation_id, reservation_no, guest_name }
  const [preCheckinSending, setPreCheckinSending] = useState(false);
  const [preCheckinEnabled, setPreCheckinEnabled] = useState(false);

  // Quick Add Rooms state
  const [quickAdd, setQuickAdd] = useState({ room_type_id: '', count: 1, adults: 2, children: 0, rate_plan_code: '' });
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [paxTarget, setPaxTarget] = useState({ adults: 0, children: 0 });

  const latestQuoteKeyRef = useRef({});
  // Set right after loadReservationForEdit() populates the form — its single
  // setForm() call also sets arrival_date/departure_date, which otherwise
  // trips the requote effect below and silently overwrites every line's
  // already-saved (possibly manually customized) rate with a fresh rate-plan
  // quote. Consumed once so a genuine, user-driven date change afterwards
  // still requotes normally.
  const skipNextRequoteRef = useRef(false);

  const headers = () => ({
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || ''
  });

  // Opens the Reservation Voucher PDF in a new tab. Called automatically right
  // after a new booking is confirmed (REQ-04), and available as a manual
  // reprint action from the reservation list.
  const openVoucher = async (reservationId) => {
    // Android WebView (the APK) can't show a blob: PDF — no PDF viewer, and a
    // blob can't be handed to another app. Navigate to a short-lived token
    // link instead; the APK's download handler / external browser opens it.
    const isAndroidWebView = typeof navigator !== 'undefined'
      && /Android/i.test(navigator.userAgent || '')
      && (/; wv\)/.test(navigator.userAgent || '') || typeof window.Android !== 'undefined');
    if (isAndroidWebView) {
      try {
        const tr = await fetch(`/api/v1/pms/reservations/${reservationId}/voucher-pdf-token`, { method: 'POST', headers: headers() });
        const td = await tr.json().catch(() => ({}));
        if (!tr.ok || !td.download_path) throw new Error(td.error || `Voucher failed (${tr.status})`);
        window.location.href = `${window.location.origin}${td.download_path}`;
      } catch (e) { setError(e.message); }
      return;
    }
    try {
      const res = await fetch(`/api/v1/pms/reservations/${reservationId}/voucher-pdf`, { headers: headers() });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Voucher failed (${res.status})`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { setError(e.message); }
  };

  // One page of Recent Reservations. Grid filters go to the server so older
  // bookings can be found; the total comes back in X-Total-Count.
  const loadReservationsPage = async (pageNo = resPage, overrides = {}) => {
    if (!selectedProperty?.id) return;
    const f = { from: resFrom, to: resTo, q: resSearch, basis: resDateBasis, ...overrides };
    const resQuery = new URLSearchParams();
    if (f.from) resQuery.set('from', f.from);
    if (f.to) resQuery.set('to', f.to);
    if (String(f.q || '').trim()) resQuery.set('q', String(f.q).trim());
    if ((f.from || f.to) && f.basis === 'booking') resQuery.set('date_basis', 'booking');
    resQuery.set('limit', String(RES_PAGE_SIZE));
    resQuery.set('offset', String(Math.max(0, pageNo) * RES_PAGE_SIZE));
    try {
      const res = await fetch(`/api/v1/pms/reservations?${resQuery.toString()}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load reservations');
      const list = Array.isArray(data) ? data : [];
      const total = Number(res.headers.get('X-Total-Count'));
      setRows(list);
      setResTotal(Number.isFinite(total) && total >= 0 ? total : list.length);
      setResPage(Math.max(0, pageNo));
    } catch (err) {
      setError(err?.message || 'Failed to load reservations');
    }
  };

  const loadData = async () => {
    if (!selectedProperty?.id) return;
    try {
      const [, typeRows, roomRows, ratePlanRows, corpRows, taRows] = await Promise.all([
        loadReservationsPage(resPage),
        fetch('/api/v1/pms/room-types', { headers: headers() }),
        fetch('/api/v1/pms/rooms', { headers: headers() }),
        fetch('/api/v1/pms/rate-plans?all=1', { headers: headers() }),
        fetch('/api/v1/pms/corporates', { headers: headers() }),
        fetch('/api/v1/pms/travel-agents', { headers: headers() })
      ]);
      const tData = await typeRows.json();
      const rmData = await roomRows.json();
      const rpData = await ratePlanRows.json();
      const corpData = await corpRows.json();
      const taData = await taRows.json();
      if (!typeRows.ok) throw new Error(tData?.error || 'Failed to load room types');
      if (!roomRows.ok) throw new Error(rmData?.error || 'Failed to load rooms');
      if (!ratePlanRows.ok) throw new Error(rpData?.error || 'Failed to load rate plans');
      if (!corpRows.ok) throw new Error(corpData?.error || 'Failed to load corporates');
      if (!taRows.ok) throw new Error(taData?.error || 'Failed to load travel agents');
      setTypes(Array.isArray(tData) ? tData : []);
      setRooms(Array.isArray(rmData) ? rmData : []);
      setRatePlans(Array.isArray(rpData) ? rpData : []);
      setCorporates(Array.isArray(corpData) ? corpData : []);
      setTravelAgents(Array.isArray(taData) ? taData : []);
    } catch (err) {
      setError(err?.message || 'Failed to load reservation data');
    }
    // Sales / Marketing team is optional on a booking — a failure here must
    // not block the reservation screen. all=1 so a member deactivated after
    // being assigned still shows on the reservations that reference them.
    try {
      const stRes = await fetch('/api/v1/pms/sales-team?all=1', { headers: headers() });
      const stData = await stRes.json();
      setSalesTeam(stRes.ok && Array.isArray(stData) ? stData : []);
    } catch (_) {
      setSalesTeam([]);
    }
  };
  const loadTaxSourceStatus = async () => {
    if (!selectedProperty?.id) {
      setRoomTaxPolicyConfigured(false);
      return;
    }
    try {
      const statusRes = await fetch('/api/v1/pms/tax-source-status', { headers: headers() });
      const statusData = await statusRes.json().catch(() => ({}));
      if (statusRes.ok) {
        setRoomTaxPolicyConfigured(statusData?.room_policy_configured === true);
        return;
      }
      const policyRes = await fetch('/api/room-tax-policy', { headers: headers() });
      const policyData = await policyRes.json().catch(() => ([]));
      if (!policyRes.ok) throw new Error('Failed to load tax source status');
      setRoomTaxPolicyConfigured((Array.isArray(policyData) ? policyData : []).some((row) => row?.is_active !== false));
    } catch (_) {
      setRoomTaxPolicyConfigured(false);
    }
  };

  const loadComplianceFields = useCallback(async () => {
    if (!selectedProperty?.id) return;
    try {
      const [fieldsRes, policyRes, countriesRes, idTypesRes] = await Promise.all([
        fetch('/api/v1/compliance/fields', { headers: headers() }),
        fetch('/api/v1/compliance/policy/guest-identity-document-rules', { headers: headers() }),
        fetch('/api/v1/core/countries', { headers: headers() }),
        fetch('/api/v1/pms/id-types', { headers: headers() })
      ]);
      const [fieldsData, policyData, countriesData, idTypesData] = await Promise.all([
        fieldsRes.json().catch(() => []),
        policyRes.json().catch(() => ({})),
        countriesRes.json().catch(() => []),
        idTypesRes.json().catch(() => [])
      ]);
      if (Array.isArray(fieldsData)) setFieldRules(fieldsData);
      if (policyRes.ok && policyData && typeof policyData === 'object') {
        setPolicyConfig(policyData);
      } else {
        setPolicyConfig({});
      }
      if (Array.isArray(countriesData)) {
        setCountryOptions(countriesData);
      } else {
        setCountryOptions([]);
      }
      setIdTypeOptions(Array.isArray(idTypesData) ? idTypesData : []);
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProperty?.id, token]);

  const loadPropertyBusinessDate = async () => {
    if (!selectedProperty?.id) return;
    try {
      const res = await fetch('/api/v1/pms/property-config', { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data || data.error) return;
      if (data.business_date && /^\d{4}-\d{2}-\d{2}$/.test(data.business_date)) {
        const arrival = data.business_date;
        const departure = addDaysIso(arrival, 1);
        setForm((f) => ({
          ...f,
          arrival_date: arrival,
          departure_date: departure,
          lines: f.lines.map((l) => ({ ...l, arrival_date: arrival, departure_date: departure }))
        }));
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    setError('');
    setMessage('');
    setStep(1);
    setComplianceGuestList([buildDefaultComplianceData(selectedProperty?.country)]);
    setActiveGuestIdx(0);
    setComplianceErrorsList([{}]);
    setOverrideMode(false);
    setOverrideReason('');
    loadData();
    loadTaxSourceStatus();
    loadComplianceFields();
    loadPropertyBusinessDate();
    // Check if pre-check-in is enabled for this property
    if (selectedProperty?.id) {
      fetch('/api/v1/pms/pre-checkin/config', { headers: { Authorization: `Bearer ${token}`, 'x-chain-id': selectedProperty?.chain_id || '', 'x-property-id': selectedProperty?.id || '' } })
        .then(r => r.json()).then(d => setPreCheckinEnabled(!!d?.is_enabled)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProperty?.id]);

  useEffect(() => {
    if (initialEditId) {
      loadReservationForEdit(initialEditId);
      if (typeof onInitialEditConsumed === 'function') onInitialEditConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEditId]);

  // Pre-fill guest details from Guest Master "Make Reservation"
  useEffect(() => {
    if (!prefillGuest) return;
    const fullName = [prefillGuest.title, prefillGuest.first_name, prefillGuest.last_name]
      .filter(v => String(v || '').trim()).join(' ');
    // Step 1 fields
    setForm(prev => ({
      ...prev,
      primary_guest_name: fullName || prev.primary_guest_name,
      mobile_number: prefillGuest.mobile_number || prev.mobile_number,
      email: prefillGuest.email || prev.email,
      // Bug 8663 — carry the guest's Company Name and GSTIN into the reservation.
      company_name: prefillGuest.company_name || prev.company_name,
      gst_number: prefillGuest.gst_number || prev.gst_number,
      billing_address: prefillGuest.billing_address || prev.billing_address,
    }));
    // Step 2 — compliance data pre-filled from guest master
    setComplianceGuestList([{
      guest_name: fullName,
      nationality: prefillGuest.nationality || selectedProperty?.country || 'India',
      is_foreign_guest: Boolean(prefillGuest.is_foreign_guest),
      gender: prefillGuest.gender || '',
      dob: prefillGuest.dob || '',
      address: prefillGuest.address || '',
      purpose_of_visit: prefillGuest.purpose_of_visit || '',
      id_proof_type: prefillGuest.id_proof_type || '',
      id_proof_no: prefillGuest.id_proof_no || '',
      id_expiry_date: prefillGuest.id_expiry_date || '',
      passport_number: prefillGuest.passport_number || '',
      visa_number: prefillGuest.visa_number || '',
      visa_expiry_date: prefillGuest.visa_expiry_date || '',
      arrival_stamp_date: prefillGuest.arrival_stamp_date || '',
      date_of_arrival_in_india: prefillGuest.date_of_arrival_in_india || '',
      place_of_issue: prefillGuest.place_of_issue || '',
      id_scan_data: prefillGuest.id_scan_data || '',
      id_scan_name: prefillGuest.id_scan_name || '',
      passport_scan_data: prefillGuest.passport_scan_data || '',
      passport_scan_name: prefillGuest.passport_scan_name || '',
    }]);
    if (typeof onPrefillGuestConsumed === 'function') onPrefillGuestConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillGuest?.ts]);

  const computeCommissionAmount = (nextForm) => {
    const base = Number((nextForm.lines || []).reduce((sum, l) => sum + Number(l.rate_amount || 0), 0));
    const pct = Number(nextForm.travel_agent_commission_percent || 0);
    if (!nextForm.travel_agent_id || !Number.isFinite(base) || !Number.isFinite(pct) || pct <= 0) return 0;
    return Number(((base * pct) / 100).toFixed(2));
  };
  const BOOKING_SOURCE_OPTIONS = [
    'DIRECT', 'PHONE', 'EMAIL', 'WEBSITE', 'WALK_IN',
    'OTA', 'GOIBIBO', 'BOOKING.COM', 'AGODA', 'MAKEMYTRIP', 'CLEARTRIP',
    'CORPORATE', 'TRAVEL_AGENT', 'SALES_TEAM', 'REFERRAL', 'OTHER'
  ];
  const normalizeBookingSource = (value) => {
    const v = String(value || '').trim().toUpperCase();
    if (BOOKING_SOURCE_OPTIONS.includes(v)) return v;
    return 'DIRECT';
  };

  const deriveQuoteForLine = async (lineIndex, nextForm, opts = {}) => {
    const line = nextForm.lines[lineIndex];
    const lineArrival = line.arrival_date || nextForm.arrival_date;
    const lineDeparture = line.departure_date || nextForm.departure_date;
    const inferredRoomId = line.room_id
      ? Number(line.room_id)
      : Number(
        rooms.find((r) => line.room_type_id && String(r.room_type_id) === String(line.room_type_id))?.room_id || 0
      );
    const canQuote = selectedProperty?.id
      && inferredRoomId > 0
      && line?.rate_plan_code
      && lineArrival
      && lineDeparture;
    // Sticky per-night override: if the caller didn't pass one explicitly but this
    // line already carries a manual per-night rate, reuse it so that changing the
    // stay dates rescales the total (per-night × new nights) instead of silently
    // reverting to the rate-plan default. Keeps rate_amount = per-night × nights.
    const overridePerNight = Number(opts.overridePerNight
      || (line?.rate_override && line?.rate_override_per_night ? line.rate_override_per_night : 0));
    const quoteKey = [
      inferredRoomId,
      String(line?.rate_plan_code || ''),
      String(lineArrival || ''),
      String(lineDeparture || ''),
      String(line?.adults ?? ''),
      String(line?.children ?? ''),
      overridePerNight > 0 ? `o:${overridePerNight}` : '',
      String(line?.tax_mode || '')
    ].join('|');
    latestQuoteKeyRef.current[lineIndex] = quoteKey;
    if (!canQuote) {
      setForm((prev) => {
        if (latestQuoteKeyRef.current[lineIndex] !== quoteKey) return prev;
        const lines = [...prev.lines];
        if (!lines[lineIndex]) return prev;
        lines[lineIndex] = { ...lines[lineIndex], rate_amount: 0, tax_amount: 0, rate_quote_error: null };
        const next = { ...prev, lines };
        return { ...next, travel_agent_commission_amount: computeCommissionAmount(next) };
      });
      return;
    }
    try {
      const res = await fetch('/api/v1/pms/walkin/rate-quote', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          room_id: inferredRoomId,
          arrival_datetime: `${lineArrival}T12:00:00`,
          departure_datetime: `${lineDeparture}T11:00:00`,
          rate_plan_code: line.rate_plan_code,
          adults: Number(line.adults || 1),
          children: Number(line.children || 0),
          ...(overridePerNight > 0 ? { rate_override_per_night: overridePerNight } : {}),
          ...(line.tax_mode ? { tax_mode: line.tax_mode } : {})
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Rate quote failed');
      setForm((prev) => {
        if (latestQuoteKeyRef.current[lineIndex] !== quoteKey) return prev;
        const lines = [...prev.lines];
        if (!lines[lineIndex]) return prev;
        lines[lineIndex] = {
          ...lines[lineIndex],
          // Lock in the effective Include/Exclude mode (plan default when the
          // user hasn't chosen) so it is saved with the line.
          tax_mode: lines[lineIndex].tax_mode || data?.tax_mode || '',
          rate_amount: Number(data?.subtotal || 0),
          tax_amount: Number(data?.tax_total || 0),
          _orig_rate_amount: Number(data?.subtotal || 0),
          _orig_tax_amount: Number(data?.tax_total || 0),
          rate_override: overridePerNight > 0,
          rate_override_per_night: overridePerNight > 0 ? overridePerNight : undefined,
          rate_quote_error: null
        };
        const next = { ...prev, lines };
        return { ...next, travel_agent_commission_amount: computeCommissionAmount(next) };
      });
    } catch (err) {
      // Bug: this used to be caught and discarded, so a legitimate rejection
      // (e.g. the rate plan's stay-date window doesn't cover this arrival
      // date) looked identical to a silent, unexplained rate-lookup failure —
      // the Rate/Night field just showed 0.00 with no indication why.
      setForm((prev) => {
        if (latestQuoteKeyRef.current[lineIndex] !== quoteKey) return prev;
        const lines = [...prev.lines];
        if (!lines[lineIndex]) return prev;
        lines[lineIndex] = { ...lines[lineIndex], rate_amount: 0, tax_amount: 0, rate_quote_error: err.message || 'Rate quote failed' };
        const next = { ...prev, lines };
        return { ...next, travel_agent_commission_amount: computeCommissionAmount(next) };
      });
    }
  };

  const updateLine = (idx, patch) => {
    // Adults >= 1, children >= 0 — clamp on every update (Bug 8695).
    if (patch.adults != null) {
      const n = parseInt(String(patch.adults), 10);
      patch = { ...patch, adults: isNaN(n) ? 1 : Math.max(1, n) };
    }
    if (patch.children != null) {
      const n = parseInt(String(patch.children), 10);
      patch = { ...patch, children: isNaN(n) ? 0 : Math.max(0, n) };
    }
    const current = form.lines[idx];
    const nextForm = {
      ...form,
      lines: form.lines.map((line, i) => (i === idx ? { ...line, ...patch } : line))
    };
    if (patch.room_id) {
      const picked = rooms.find((r) => String(r.room_id) === String(patch.room_id));
      if (picked?.room_type_id) {
        nextForm.lines[idx].room_type_id = String(picked.room_type_id);
      }
    }
    if (patch.room_type_id && String(patch.room_type_id) !== String(current.room_type_id)) {
      nextForm.lines[idx].room_id = '';
      nextForm.lines[idx].rate_plan_code = '';
      nextForm.lines[idx].rate_amount = 0;
    }
    // A new rate plan starts from that plan's own Include/Exclude GST default.
    if (patch.rate_plan_code !== undefined && String(patch.rate_plan_code) !== String(current.rate_plan_code || '')) {
      nextForm.lines[idx].tax_mode = '';
    }
    // Skip rate-quote if user manually overrode the rate
    if (patch.rate_override) {
      nextForm.travel_agent_commission_amount = computeCommissionAmount(nextForm);
      setForm(nextForm);
    } else {
      // Pricing context (room/rate-plan/pax) changed → drop any manual override
      // and re-quote at the rate-plan default. A DATE-only change keeps the
      // override so deriveQuoteForLine rescales per-night × new nights (prevents
      // the stale stay-total that under-billed INV-202607-00045).
      const pricingContextChanged = patch.room_type_id || patch.room_id || patch.rate_plan_code || patch.adults != null || patch.children != null;
      if (pricingContextChanged && editingOtaInfo) {
        // OTA reservations carry an OTA-negotiated rate, not a rate-plan rate.
        // A routine edit here (reassigning the room, correcting pax, etc.)
        // must never substitute a walk-in rate-plan quote for it — that
        // silently inflates the room tariff posted at check-in/night-audit
        // while the OTA-Prepaid credit still reflects the true (lower) OTA
        // amount, leaving an unexplained gap on the folio. Apply the room/pax
        // change itself but leave rate_amount/tax_amount exactly as saved.
        setForm(nextForm);
        return;
      }
      if (pricingContextChanged) {
        nextForm.lines[idx].rate_override = false;
        nextForm.lines[idx].rate_override_per_night = undefined;
        nextForm.lines[idx]._orig_rate_amount = undefined;
        nextForm.lines[idx]._orig_tax_amount = undefined;
      }
      setForm(nextForm);
      deriveQuoteForLine(idx, nextForm);
    }
  };

  const addLine = () => setForm((prev) => ({ ...prev, lines: [...prev.lines, { ...emptyLine, arrival_date: prev.arrival_date, departure_date: prev.departure_date }] }));
  const removeLine = (idx) => setForm((prev) => {
    latestQuoteKeyRef.current = {};
    const next = prev.lines.filter((_, i) => i !== idx);
    return { ...prev, lines: next.length ? next : [{ ...emptyLine, arrival_date: prev.arrival_date, departure_date: prev.departure_date }] };
  });

  const addQuickRooms = () => {
    const { room_type_id, count, adults, children, rate_plan_code } = quickAdd;
    if (!room_type_id || count < 1) return;
    const qty = Math.min(Math.max(1, Number(count)), 50);
    const totalAdults = Math.max(0, Number(adults || 0));
    const totalChildren = Math.max(0, Number(children || 0));

    const selectedType = types.find((rt) => String(rt.room_type_id) === String(room_type_id));
    const maxA = selectedType ? Math.max(1, Number(selectedType.max_adults || 2)) : 99;
    const maxC = selectedType ? Math.max(0, Number(selectedType.max_children || 2)) : 99;
    const maxPax = selectedType ? Math.max(1, Number(selectedType.max_pax || 3)) : 99;

    // Distribute adults evenly across rooms, clamped by room type capacity
    const baseAdults = qty > 0 ? Math.floor(totalAdults / qty) : 1;
    let remainderAdults = qty > 0 ? totalAdults - (baseAdults * qty) : 0;
    const baseChildren = qty > 0 ? Math.floor(totalChildren / qty) : 0;
    let remainderChildren = qty > 0 ? totalChildren - (baseChildren * qty) : 0;

    const newLines = Array.from({ length: qty }, (_, i) => {
      let a = baseAdults + (remainderAdults > 0 ? 1 : 0);
      if (remainderAdults > 0) remainderAdults--;
      let c = baseChildren + (remainderChildren > 0 ? 1 : 0);
      if (remainderChildren > 0) remainderChildren--;
      // Clamp by room type capacity
      a = Math.min(Math.max(1, a), maxA);
      c = Math.min(Math.max(0, c), maxC);
      if (a + c > maxPax) c = Math.max(0, maxPax - a);
      return {
        ...emptyLine,
        room_type_id: String(room_type_id),
        adults: a,
        children: c,
        rate_plan_code: rate_plan_code || '',
        arrival_date: form.arrival_date,
        departure_date: form.departure_date
      };
    });

    // Track requested totals for remaining-pax banner
    setPaxTarget((prev) => ({
      adults: prev.adults + totalAdults,
      children: prev.children + totalChildren
    }));

    setForm((prev) => {
      const existingNonEmpty = prev.lines.filter((l) => l.room_type_id);
      const merged = existingNonEmpty.length ? [...prev.lines, ...newLines] : newLines;
      return { ...prev, lines: merged };
    });
    // Trigger rate quotes for new lines after state update
    setTimeout(() => {
      setForm((prev) => {
        const startIdx = prev.lines.length - qty;
        for (let i = startIdx; i < prev.lines.length; i++) {
          deriveQuoteForLine(i, prev);
        }
        return prev;
      });
    }, 100);
    setQuickAddOpen(false);
    setQuickAdd({ room_type_id: '', count: 1, adults: 2, children: 0, rate_plan_code: '' });
  };

  const openCancelModal = async (r) => {
    setCancelModal(r);
    setCancelLoading(true);
    setCancelPreview(null);
    setCancelError('');
    setCancelForm({ reason: '', overridePenalty: false });
    setCancelAdvanceNet(0);

    try {
      const [previewRes, advRes] = await Promise.all([
        fetch(`/api/v1/pms-cancellation/reservations/${r.reservation_id}/preview`, { headers: headers() }),
        fetch(`/api/v1/pms/reservations/${r.reservation_id}/advances`, { headers: headers() })
      ]);
      let data;
      const text = await previewRes.text();
      try { data = JSON.parse(text); } catch { throw new Error(text || 'Server/Proxy error'); }
      if (!previewRes.ok || !data.success) throw new Error(data.message || 'Failed to get cancellation preview');
      setCancelPreview(data.data);
      const advData = advRes.ok ? await advRes.json() : {};
      setCancelAdvanceNet(Number(advData.net_advance || 0));
    } catch (err) {
      setCancelError(err.message);
    } finally {
      setCancelLoading(false);
    }
  };

  const submitCancellation = async () => {
    if (!cancelModal) return;
    setCancelLoading(true);
    setCancelError('');
    try {
      const res = await fetch(`/api/v1/pms-cancellation/reservations/${cancelModal.reservation_id}/cancel`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          reason: cancelForm.reason,
          overridePenalty: cancelForm.overridePenalty
        })
      });
      let data;
      const text = await res.text();
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(text || 'Server/Proxy error');
      }
      if (!res.ok || !data.success) throw new Error(data.message || 'Cancellation failed');
      // Success
      const cancelledRow = { ...cancelModal, status: 'CANCELLED' };
      const hadAdvance = cancelAdvanceNet > 0;
      setMessage(`Reservation ${data.reservationId} cancelled successfully.${hadAdvance ? ` Outstanding advance of ₹${cancelAdvanceNet.toFixed(2)} — issue refund below.` : ''}`);
      setCancelModal(null);
      loadData(); // Reload list
      if (hadAdvance) {
        // Auto-open advance modal with refund panel pre-expanded
        openAdvanceModal(cancelledRow, { autoShowRefund: true });
      }
    } catch (err) {
      setCancelError(err.message);
    } finally {
      setCancelLoading(false);
    }
  };

  const loadReservationForEdit = async (reservationId) => {
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/v1/pms/reservations/${reservationId}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to load reservation');
      setEditingId(String(reservationId));
      skipNextRequoteRef.current = true;
      const bookingSource = data.travel_agent_id
        ? 'TRAVEL_AGENT'
        : (data.corporate_id ? 'CORPORATE' : normalizeBookingSource(data.booking_source));
      setForm({
        booking_source: bookingSource,
        status: data.status || 'TENTATIVE',
        corporate_id: data.corporate_id ? String(data.corporate_id) : '',
        travel_agent_id: data.travel_agent_id ? String(data.travel_agent_id) : '',
        travel_agent_commission_percent: Number(data.travel_agent_commission_percent || 0),
        travel_agent_commission_amount: Number(data.travel_agent_commission_amount || 0),
        bill_to: data.bill_to || (data.corporate_id ? 'CORPORATE' : 'SELF'),
        credit_hold_amount: data.credit_hold_amount ? String(Number(data.credit_hold_amount || 0).toFixed(2)) : '',
        arrival_date: toDateInputValue(data.arrival_date),
        departure_date: toDateInputValue(data.departure_date),
        primary_guest_name: data.primary_guest_name || '',
        mobile_number: data.mobile_number || '',
        email: data.email || '',
        gst_number: data.gst_number || '',
        company_name: data.company_name || '',
        billing_address: data.billing_address || '',
        // Previously missing here — GET didn't return these either (fixed
        // alongside), so reopening a corporate reservation for edit always
        // showed these blank even when they'd been saved, and saving again
        // silently wiped them out.
        employee_id: data.employee_id || '',
        vessel_name: data.vessel_name || '',
        care_of: data.care_of || '',
        sales_member_id: data.sales_member_id ? String(data.sales_member_id) : '',
        room_count: Number(data.room_count || 1),
        total_pax: Number(data.total_pax || 1),
        lines: Array.isArray(data.lines) && data.lines.length
          ? data.lines.map((l) => ({
            room_type_id: String(l.room_type_id || ''),
            room_id: l.room_id ? String(l.room_id) : '',
            arrival_date: toDateInputValue(l.arrival_date || data.arrival_date),
            departure_date: toDateInputValue(l.departure_date || data.departure_date),
            // Bug 8695 — heal any legacy negative pax when loading for edit.
            adults: Math.max(1, Number(l.adults || 1)),
            children: Math.max(0, Number(l.children || 0)),
            rate_plan_code: l.rate_plan_code || '',
            tax_mode: l.tax_mode || '',
            rate_amount: Number(l.rate_amount || 0),
            tax_amount: Number(l.tax_amount || 0)
          }))
          : [{ ...emptyLine, arrival_date: toDateInputValue(data.arrival_date), departure_date: toDateInputValue(data.departure_date) }]
      });
      // Bug 8662 — make it obvious the form switched to edit mode: scroll it into
      // view and briefly highlight it so the agent sees the reservation loaded.
      setEditHighlight(true);
      setTimeout(() => { editFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 60);
      setTimeout(() => setEditHighlight(false), 2000);
      // Store OTA info if this is a channel-manager booking
      if (data.channel_booking_id) {
        setEditingOtaInfo({
          channel_booking_id: data.channel_booking_id,
          ota_booking_amount: Number(data.ota_booking_amount || 0),
          ota_tax_amount: Number(data.ota_tax_amount || 0),
          ota_net_rate: Number(data.ota_net_rate || 0),
          pay_at_hotel: data.pay_at_hotel !== false,
          booking_source: data.booking_source
        });
      } else {
        setEditingOtaInfo(null);
      }
      setMessage(`Editing reservation ${data.reservation_no || `RES-${reservationId}`}`);
      // Prefill compliance data from guest master fields returned with reservation
      const additionalGuestsIn = Array.isArray(data.additional_guests) ? data.additional_guests : [];
      if (data.gender || data.dob || data.id_proof_type || data.id_proof_no || data.nationality || data.address
          || additionalGuestsIn.length > 0) {
        setComplianceGuestList([
          {
            gender: data.gender || '',
            dob: data.dob || '',
            nationality: data.nationality || selectedProperty?.country || 'India',
            is_foreign_guest: Boolean(data.is_foreign_guest),
            address: data.address || '',
            purpose_of_visit: data.purpose_of_visit || '',
            id_proof_type: data.id_proof_type || '',
            id_proof_no: data.id_proof_no || '',
            id_expiry_date: data.id_expiry_date || '',
            passport_number: data.passport_number || '',
            visa_number: data.visa_number || '',
            visa_expiry_date: data.visa_expiry_date || '',
            arrival_stamp_date: data.arrival_stamp_date || '',
            date_of_arrival_in_india: data.date_of_arrival_in_india || '',
            place_of_issue: data.place_of_issue || '',
            id_scan_data: '',
            id_scan_name: '',
            passport_scan_data: '',
            passport_scan_name: '',
          },
          // Guests 2+ — previously never round-tripped back into the edit
          // form, so every save silently dropped them (they were only ever
          // seen once, right after the guest was added, before the next
          // reload wiped them out of the form's in-memory state).
          ...additionalGuestsIn.map((ag) => ({
            guest_name: ag.guest_name || '',
            // guest_id links this guest to their own Guest Master profile —
            // carried through unchanged so re-saving updates that SAME
            // profile instead of minting a new, un-linked one every edit.
            guest_id: ag.guest_id || null,
            mobile_number: ag.mobile_number || '',
            email: ag.email || '',
            gender: ag.gender || '',
            dob: ag.dob || '',
            age_years: ag.age_years != null ? String(ag.age_years) : '',
            nationality: ag.nationality || selectedProperty?.country || 'India',
            is_foreign_guest: Boolean(ag.is_foreign_guest),
            address: ag.address || '',
            purpose_of_visit: ag.purpose_of_visit || '',
            id_proof_type: ag.id_proof_type || '',
            id_proof_no: ag.id_proof_no || '',
            id_expiry_date: ag.id_expiry_date || '',
            passport_number: ag.passport_number || '',
            visa_number: ag.visa_number || '',
            visa_expiry_date: ag.visa_expiry_date || '',
            arrival_stamp_date: ag.arrival_stamp_date || '',
            date_of_arrival_in_india: ag.date_of_arrival_in_india || '',
            place_of_issue: ag.place_of_issue || '',
            cform_submission_status: ag.cform_submission_status || 'PENDING',
            // Carry the already-saved scan reference through unchanged (it's
            // a stored path, not raw image bytes) so re-saving this guest
            // without touching the upload doesn't null out the existing
            // scan — see processScanUpload() in documentStorage.js, which
            // passes any non-"data:" value through untouched.
            id_scan_data: ag.id_scan_data || '',
            id_scan_name: ag.id_scan_name || '',
            passport_scan_data: ag.passport_scan_data || '',
            passport_scan_name: ag.passport_scan_name || '',
          })),
        ]);
        setActiveGuestIdx(0);
      }
      // Load existing advance summary
      try {
        const advRes = await fetch(`/api/v1/pms/reservations/${reservationId}/advances`, { headers: headers() });
        if (advRes.ok) {
          const advData = await advRes.json();
          setEditingAdvanceSummary({ net_advance: Number(advData.net_advance || 0), transactions: advData.transactions || [], voided: advData.voided || [] });
        } else { setEditingAdvanceSummary(null); }
      } catch { setEditingAdvanceSummary(null); }
    } catch (err) {
      setError(err?.message || 'Failed to load reservation');
    }
  };

  // Shared validation for step 1 fields
  const validateStep1 = () => {
    if (!selectedProperty?.id) { setError('Select property first'); return false; }
    if (!form.primary_guest_name) { setError('Guest Name is required'); return false; }
    if (!editingId && form.arrival_date && form.arrival_date < toIsoDate(new Date())) {
      setError('Arrival date cannot be in the past for a new reservation'); return false;
    }
    const mobileError = validateMobile(form.mobile_number);
    if (mobileError) { setError(mobileError); return false; }
    const emailVal = String(form.email || '').trim();
    if (emailVal && !/^[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,10}$/.test(emailVal)) { setError('Enter a valid email address'); return false; }
    const gstVal = String(form.gst_number || '').trim();
    if (gstVal && !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gstVal)) { setError('Invalid GSTIN format. Must be 15 characters (e.g. 22AAAAA0000A1Z5)'); return false; }
    if (normalizeBookingSource(form.booking_source) === 'CORPORATE' && !form.corporate_id) {
      setError('Please select Corporate booking source value'); return false;
    }
    if (normalizeBookingSource(form.booking_source) === 'TRAVEL_AGENT' && !form.travel_agent_id) {
      setError('Please select Travel Agent booking source value'); return false;
    }
    if (normalizeBookingSource(form.booking_source) === 'SALES_TEAM' && !form.sales_member_id) {
      setError('Please select the Sales Team Member'); return false;
    }
    if (normalizeBookingSource(form.booking_source) === 'TRAVEL_AGENT' && form.travel_agent_id === '__OTHERS__' && !adhocAgentName.trim()) {
      setError('Please enter the ad-hoc travel agent name'); return false;
    }
    const invalidLine = form.lines.find((l) => !l.room_type_id);
    if (invalidLine) { setError('Each room line must have a room type selected.'); return false; }
    return true;
  };

  // Resolve ad-hoc travel agent: create in master and return real ID
  const resolveAdhocAgent = async () => {
    if (form.travel_agent_id !== '__OTHERS__') return form.travel_agent_id;
    const res = await fetch('/api/v1/pms/travel-agents/adhoc', {
      method: 'POST',
      headers: { ...headers(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        agent_name: adhocAgentName.trim(),
        commission_percent: Number(adhocAgentCommission || 0),
      }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to create ad-hoc travel agent');
    // Add to local list so it appears in dropdown
    setTravelAgents((prev) => [...prev, data]);
    // Update form with the real ID
    setForm((prev) => ({
      ...prev,
      travel_agent_id: String(data.travel_agent_id),
      travel_agent_commission_percent: Number(data.commission_percent || adhocAgentCommission || 0),
    }));
    setAdhocAgentName('');
    setAdhocAgentCommission('');
    return String(data.travel_agent_id);
  };

  // Create reservation directly from step 1 (skip compliance — for pre-check-in flow)
  const createReservationDirectInner = async () => {
    if (!validateStep1()) return;
    setError('');
    setMessage('');
    try {
      // Resolve ad-hoc agent if needed
      let resolvedAgentId = form.travel_agent_id;
      if (normalizeBookingSource(form.booking_source) === 'TRAVEL_AGENT' && form.travel_agent_id === '__OTHERS__') {
        resolvedAgentId = await resolveAdhocAgent();
      }
      const advAmt = Number(form.advance_amount || 0);
      const payload = {
        ...form,
        booking_source: normalizeBookingSource(form.booking_source),
        corporate_id: form.corporate_id ? Number(form.corporate_id) : null,
        travel_agent_id: resolvedAgentId && resolvedAgentId !== '__OTHERS__' ? Number(resolvedAgentId) : null,
        travel_agent_commission_percent: Number(form.travel_agent_commission_percent || 0),
        bill_to: form.corporate_id ? form.bill_to : 'SELF',
        credit_hold_amount: form.bill_to === 'SPLIT' ? Number(form.credit_hold_amount || 0) : undefined,
        room_count: Number(form.lines.length),
        total_pax: Number(form.lines.reduce((sum, l) => sum + Number(l.adults || 0) + Number(l.children || 0), 0)),
        advance_amount: advAmt > 0 ? advAmt : undefined,
        advance_payment_mode: advAmt > 0 ? (form.advance_payment_mode || 'CASH') : undefined,
        advance_reference_no: advAmt > 0 ? (form.advance_reference_no || '') : undefined,
        advance_remarks: advAmt > 0 ? (form.advance_remarks || 'Advance at booking') : undefined,
        lines: form.lines.map((l) => ({
          ...l,
          room_type_id: Number(l.room_type_id),
          room_id: l.room_id ? Number(l.room_id) : null,
          adults: Number(l.adults || 1),
          children: Number(l.children || 0),
          rate_amount: Number(l.rate_amount || 0),
          tax_amount: Number(l.tax_amount || 0)
        }))
      };
      const url = editingId ? `/api/v1/pms/reservations/${editingId}` : '/api/v1/pms/reservations';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: headers(), body: JSON.stringify(payload) });
      const data = await res.json();
      if (res.status === 409 && data?.restrictions?.length) {
        setRestrictionModal({ restrictions: data.restrictions, payload, url, method });
        return;
      }
      if (!res.ok) throw new Error(data?.error || `Failed to ${editingId ? 'update' : 'create'} reservation`);
      const wasCreating = !editingId;
      setMessage(`${editingId ? 'Reservation updated' : 'Reservation created'}: ${data.reservation_no}`);
      setForm(buildEmptyForm());
      loadPropertyBusinessDate();
      setEditingId('');
      setEditingOtaInfo(null);
      setStep(1);
      loadData();
      if (wasCreating && preCheckinEnabled && data.reservation_id) {
        setPreCheckinPrompt({ reservation_id: data.reservation_id, reservation_no: data.reservation_no, guest_name: payload.primary_guest_name || '' });
      }
    } catch (err) {
      setError(err?.message || 'Failed to create reservation');
    }
  };

  const goToComplianceStep = async () => {
    if (!validateStep1()) return;
    setError('');
    // Resolve ad-hoc travel agent before moving to step 2
    if (normalizeBookingSource(form.booking_source) === 'TRAVEL_AGENT' && form.travel_agent_id === '__OTHERS__') {
      try { await resolveAdhocAgent(); } catch (err) { setError(err.message); return; }
    }
    const isEachGuest = policyConfig?.domestic_document_mode === 'EACH_GUEST';
    const totalAdults = form.lines.reduce((sum, l) => sum + Number(l.adults || 0), 0);
    const guestList = buildGuestList(
      selectedProperty?.country,
      form.primary_guest_name,
      totalAdults,
      isEachGuest
    );

    // Pre-fill from already-loaded guest master data (when editing an existing reservation)
    if (editingId && complianceGuestList.length > 0) {
      const existingPrimary = complianceGuestList[0];
      const hasExistingData = existingPrimary && (
        existingPrimary.gender || existingPrimary.dob || existingPrimary.id_proof_type ||
        existingPrimary.id_proof_no || existingPrimary.nationality || existingPrimary.address
      );
      if (hasExistingData) {
        guestList[0] = { ...guestList[0], ...existingPrimary };
      }
    }

    // Pre-fill from pre-checkin data if available (editing existing reservation)
    // Pre-checkin data overrides guest master data where present
    if (editingId) {
      try {
        const pciRes = await fetch(`/api/v1/pms/pre-checkin/data/${editingId}`, { headers: headers() });
        if (pciRes.ok) {
          const pciData = await pciRes.json();
          const guests = Array.isArray(pciData.guests) ? pciData.guests : [];
          guests.forEach((g, idx) => {
            if (idx < guestList.length && g.guest_data) {
              const d = g.guest_data;
              guestList[idx] = {
                ...guestList[idx],
                gender: d.gender || guestList[idx].gender || '',
                dob: d.dob || guestList[idx].dob || '',
                address: d.address || guestList[idx].address || '',
                purpose_of_visit: d.purpose_of_visit || guestList[idx].purpose_of_visit || '',
                nationality: d.nationality || guestList[idx].nationality || '',
                is_foreign_guest: d.is_foreign_guest || guestList[idx].is_foreign_guest || false,
                id_proof_type: d.id_proof_type || guestList[idx].id_proof_type || '',
                id_proof_no: d.id_proof_no || guestList[idx].id_proof_no || '',
                id_expiry_date: d.id_expiry_date || guestList[idx].id_expiry_date || '',
                passport_number: d.passport_number || guestList[idx].passport_number || '',
                visa_number: d.visa_number || guestList[idx].visa_number || '',
                visa_expiry_date: d.visa_expiry_date || guestList[idx].visa_expiry_date || '',
                arrival_stamp_date: d.arrival_stamp_date || guestList[idx].arrival_stamp_date || '',
                date_of_arrival_in_india: d.date_of_arrival_in_india || guestList[idx].date_of_arrival_in_india || '',
                place_of_issue: d.place_of_issue || guestList[idx].place_of_issue || '',
                guest_name: idx === 0 ? form.primary_guest_name : (d.guest_name || guestList[idx].guest_name || ''),
              };
            }
          });
        }
      } catch { /* pre-checkin data not available — proceed with empty form */ }
    }

    setComplianceGuestList(guestList);
    setActiveGuestIdx(0);
    setComplianceErrorsList(Array(guestList.length).fill({}));
    setStep(2);
  };

  const validateSingleGuest = (guestData, guestIdx) => {
    const hotelCountry = selectedProperty?.country || 'India';
    const normalizedHotelCountry = normalizeCountry(hotelCountry);
    const normalizedNationality = normalizeCountry(guestData.nationality);
    const isForeign = Boolean(guestData.is_foreign_guest) || (
      normalizedNationality
        ? (normalizedHotelCountry ? normalizedNationality !== normalizedHotelCountry : (normalizedNationality !== 'india' && normalizedNationality !== 'indian'))
        : false
    );
    const errs = {};
    if (guestIdx > 0 && !guestData.guest_name) {
      errs.guest_name = 'Guest Name is required';
    }
    fieldRules.forEach((r) => {
      if (!r.is_mandatory || !r.is_visible) return;
      const appliesToThis = r.applies_to === 'ALL' ||
        (r.applies_to === 'FOREIGN' && isForeign) ||
        (r.applies_to === 'INDIAN' && !isForeign);
      if (isForeign && (r.field_name === 'id_proof_type' || r.field_name === 'id_proof_no')) return;
      if (r.field_name === 'age_years') return; // age field removed
      if (appliesToThis && !guestData[r.field_name]) {
        errs[r.field_name] = `${r.label || r.field_name} is required`;
      }
    });
    const todayStr = new Date().toISOString().slice(0, 10);
    if (isForeign) {
      if (guestData.visa_expiry_date && guestData.visa_expiry_date < todayStr) {
        errs.visa_expiry_date = 'Visa Expiry Date cannot be in the past';
      }
    } else {
      if (guestData.id_expiry_date && guestData.id_expiry_date < todayStr) {
        errs.id_expiry_date = 'ID Expiry Date cannot be in the past';
      }
    }
    return errs;
  };

  const validateComplianceFields = () => {
    // Only primary guest (index 0) is mandatory; additional guests are optional
    return complianceGuestList.map((g, idx) => {
      if (idx === 0) return validateSingleGuest(g, idx);
      return {}; // skip validation for additional guests
    });
  };

  const submitInner = async () => {
    const errorsList = validateComplianceFields();
    const totalMissing = errorsList.reduce((sum, e) => sum + Object.keys(e).length, 0);
    const hasMissing = totalMissing > 0;

    if (hasMissing && !overrideMode) {
      setComplianceErrorsList(errorsList);
      const firstErrIdx = errorsList.findIndex((e) => Object.keys(e).length > 0);
      if (firstErrIdx >= 0) setActiveGuestIdx(firstErrIdx);
      return;
    }

    // Validate all lines have a room type selected
    const invalidLine = form.lines.find((l) => !l.room_type_id);
    if (invalidLine) return setError('Each room line must have a room type selected.');

    setError('');
    setMessage('');
    try {
      if (hasMissing && overrideMode) {
        if (!overrideReason.trim()) return setError('Please provide an override reason.');
        const allMissingFields = errorsList.flatMap((e) => Object.keys(e));
        await fetch('/api/v1/compliance/override', {
          method: 'POST',
          headers: headers(),
          body: JSON.stringify({ override_reason: overrideReason, fields: allMissingFields })
        });
      }

      const [primaryGuest, ...additionalGuests] = complianceGuestList;

      // When override is active, suppress pre-filled compliance values from a returning
      // guest's prior stay so they don't leak onto this reservation's police-report row.
      // Backend stamps compliance_override_reason on the reservation header so the report
      // masks even any field that does survive (defence in depth).
      const COMPLIANCE_FIELDS_TO_BLANK = [
        'address', 'purpose_of_visit',
        'id_proof_type', 'id_proof_no', 'id_expiry_date',
        'passport_number', 'visa_number', 'visa_expiry_date',
        'arrival_stamp_date', 'date_of_arrival_in_india', 'place_of_issue',
        'id_scan_data', 'id_scan_name', 'passport_scan_data', 'passport_scan_name',
        'cform_submission_status'
      ];
      const sanitizeForOverride = (g) => {
        if (!g) return g;
        const out = { ...g };
        for (const f of COMPLIANCE_FIELDS_TO_BLANK) out[f] = null;
        return out;
      };
      const usingOverride = hasMissing && overrideMode;
      const cleanedPrimary = usingOverride ? sanitizeForOverride(primaryGuest) : primaryGuest;
      const cleanedAdditional = usingOverride ? additionalGuests.map(sanitizeForOverride) : additionalGuests;

      const payload = {
        ...form,
        // normalizeBookingSource() only recognizes the fixed dropdown list
        // (DIRECT, WALK_IN, OTA, ...) — an OTA channel's actual source string
        // (e.g. "GOIBIBO", "MAKEMYTRIP") isn't in it, so loadReservationForEdit
        // already coerced form.booking_source down to "DIRECT" the moment this
        // edit screen opened. Saving that would silently overwrite the real
        // OTA source on the reservation. editingOtaInfo.booking_source still
        // holds the untouched original value — use it for OTA bookings.
        booking_source: editingOtaInfo ? editingOtaInfo.booking_source : normalizeBookingSource(form.booking_source),
        corporate_id: form.corporate_id ? Number(form.corporate_id) : null,
        travel_agent_id: form.travel_agent_id ? Number(form.travel_agent_id) : null,
        travel_agent_commission_percent: Number(form.travel_agent_commission_percent || 0),
        bill_to: form.corporate_id ? form.bill_to : 'SELF',
        credit_hold_amount: form.bill_to === 'SPLIT' ? Number(form.credit_hold_amount || 0) : undefined,
        room_count: Number(form.lines.length),
        total_pax: Number(form.lines.reduce((sum, l) => sum + Number(l.adults || 0) + Number(l.children || 0), 0)),
        lines: form.lines.map((l) => ({
          ...l,
          room_type_id: Number(l.room_type_id),
          room_id: l.room_id ? Number(l.room_id) : null,
          adults: Number(l.adults || 1),
          children: Number(l.children || 0),
          rate_amount: Number(l.rate_amount || 0),
          tax_amount: Number(l.tax_amount || 0)
        })),
        // Primary guest compliance
        ...cleanedPrimary,
        // Additional guests
        additional_guests: cleanedAdditional.length > 0 ? cleanedAdditional : undefined,
        compliance_override_reason: usingOverride ? overrideReason.trim() : undefined
      };
      const url = editingId ? `/api/v1/pms/reservations/${editingId}` : '/api/v1/pms/reservations';
      const method = editingId ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: headers(),
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.status === 409 && data?.restrictions?.length) {
        // Show restriction warning modal instead of error
        setRestrictionModal({ restrictions: data.restrictions, payload, url, method });
        return;
      }
      if (!res.ok) throw new Error(data?.error || `Failed to ${editingId ? 'update' : 'create'} reservation`);
      const wasCreating = !editingId;
      const createdResNo = data.reservation_no;
      const createdResId = data.reservation_id;
      setMessage(`${editingId ? 'Reservation updated' : 'Reservation created'}: ${createdResNo}`);
      setForm(buildEmptyForm());
      loadPropertyBusinessDate();
      setEditingId('');
      setEditingOtaInfo(null);
      setStep(1);
      setComplianceGuestList([buildDefaultComplianceData(selectedProperty?.country)]);
      setActiveGuestIdx(0);
      setComplianceErrorsList([{}]);
      setOverrideMode(false);
      setOverrideReason('');
      loadData();
      // Generate the Reservation Voucher immediately on booking confirmation (REQ-04)
      if (wasCreating && createdResId) {
        openVoucher(createdResId);
      }
      // Prompt to send pre-check-in link after new reservation creation
      if (wasCreating && preCheckinEnabled && createdResId) {
        setPreCheckinPrompt({ reservation_id: createdResId, reservation_no: createdResNo, guest_name: payload.primary_guest_name || '' });
      }
    } catch (err) {
      setError(err?.message || `Failed to ${editingId ? 'update' : 'create'} reservation`);
    }
  };

  const submitWithRestrictionOverride = async () => {
    if (!restrictionModal) return;
    setRestrictionBusy(true);
    try {
      const overridePayload = { ...restrictionModal.payload, override_restrictions: true };
      const res = await fetch(restrictionModal.url, {
        method: restrictionModal.method,
        headers: headers(),
        body: JSON.stringify(overridePayload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Override failed');
      const wasCreating = !editingId;
      setRestrictionModal(null);
      setMessage(`${editingId ? 'Reservation updated' : 'Reservation created'} (restrictions overridden): ${data.reservation_no}`);
      setForm(buildEmptyForm());
      loadPropertyBusinessDate();
      setEditingId('');
      setEditingOtaInfo(null);
      setStep(1);
      setComplianceGuestList([buildDefaultComplianceData(selectedProperty?.country)]);
      setActiveGuestIdx(0);
      setComplianceErrorsList([{}]);
      setOverrideMode(false);
      setOverrideReason('');
      loadData();
      if (wasCreating && data.reservation_id) {
        openVoucher(data.reservation_id);
      }
      if (wasCreating && preCheckinEnabled && data.reservation_id) {
        setPreCheckinPrompt({ reservation_id: data.reservation_id, reservation_no: data.reservation_no, guest_name: '' });
      }
    } catch (err) {
      setError(err?.message || 'Override failed');
      setRestrictionModal(null);
    } finally {
      setRestrictionBusy(false);
    }
  };

  const copyReservation = async (reservationId) => {
    setError('');
    setMessage('');
    try {
      // If original arrival is in the past, shift dates forward to today
      const originalRow = rows.find((r) => String(r.reservation_id) === String(reservationId));
      const originalArrival = originalRow?.arrival_date ? String(originalRow.arrival_date).slice(0, 10) : null;
      let shiftDays = 0;
      if (originalArrival) {
        const today = toIsoDate(new Date());
        if (originalArrival < today) {
          const a = new Date(`${originalArrival}T00:00:00`);
          const t = new Date(`${today}T00:00:00`);
          shiftDays = Math.round((t - a) / 86400000);
        }
      }
      const res = await fetch(`/api/v1/pms/reservations/${reservationId}/copy`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ shift_days: shiftDays })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Copy failed');
      loadData();
      await loadReservationForEdit(data.reservation_id);
      setMessage(`Copied to ${data.reservation_no} — update dates/details and save`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(err?.message || 'Copy failed');
    }
  };

  const updateStatus = async (reservationId) => {
    const status = statusDraft[reservationId] || '';
    if (!status) return;
    setError('');
    setMessage('');
    // REQ-20 (docs/SRS_AMBERPARAMOUNT_REQ08-15.md §15) — this status dropdown
    // is the ONLY way to cancel an already-CHECKED_IN reservation (the
    // dedicated Cancel button/modal, with its own advance warning, is
    // disabled for that status — see the Cancel button's `disabled` prop
    // below). Unlike that modal, this path used to submit silently with no
    // warning that money was already collected. Warn here first, reusing the
    // same advances lookup the other cancel path already calls.
    if (status === 'CANCELLED') {
      try {
        const advRes = await fetch(`/api/v1/pms/reservations/${reservationId}/advances`, { headers: headers() });
        const advData = advRes.ok ? await advRes.json() : {};
        const netAdvance = Number(advData.net_advance || 0);
        if (netAdvance > 0) {
          const proceed = window.confirm(
            `⚠️ Advance Collected: ₹${netAdvance.toFixed(2)} — a refund will need to be processed after cancelling. Continue?`
          );
          if (!proceed) return;
        }
      } catch (_) { /* advances lookup is best-effort; never block cancellation on it failing */ }
    }
    try {
      const res = await fetch(`/api/v1/pms/reservations/${reservationId}/status`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ status })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Status update failed');
      setMessage(`Status updated to ${data.status}`);
      loadData();
      // REQ-20 Item 2 — unify both cancel paths' post-cancel behavior: auto-open
      // the refund panel here too, same as the dedicated Cancel modal already does.
      if (status === 'CANCELLED' && Number(data.net_advance_outstanding || 0) > 0) {
        openAdvanceModal({ reservation_id: reservationId, status: 'CANCELLED' }, { autoShowRefund: true });
      }
    } catch (err) {
      setError(err?.message || 'Status update failed');
    }
  };

  const openCiModal = async (row) => {
    const eligible = ['CONFIRMED', 'GUARANTEED'];
    // In-house reservation with rooms not yet checked in (multi-room booking
    // checked in from one room card, or rooms added after arrival).
    const isFollowUp = String(row?.status || '').toUpperCase() === 'CHECKED_IN' && Number(row?.pending_checkin_rooms || 0) > 0;
    if (!eligible.includes(String(row?.status || '').toUpperCase()) && !isFollowUp) return;
    const now = new Date();
    const arrDate = row.arrival_date ? String(row.arrival_date).slice(0, 10) : toIsoDate(now);
    const depDate = row.departure_date ? String(row.departure_date).slice(0, 10) : arrDate;
    setCiModal(row);
    setCiError('');
    setCiResult(null);
    setCiNeedsRoom(false);
    setCiLines([]);
    // Load net advance + all room lines
    try {
      const [advRes, detRes] = await Promise.all([
        fetch(`/api/v1/pms/reservations/${row.reservation_id}/advances`, { headers: headers() }),
        fetch(`/api/v1/pms/reservations/${row.reservation_id}`, { headers: headers() })
      ]);
      const advData = advRes.ok ? await advRes.json() : {};
      const detData = detRes.ok ? await detRes.json() : {};
      const netAdv = Number(advData.net_advance || 0);
      const allLines = (Array.isArray(detData.lines) ? detData.lines : [])
        .filter((l) => !isFollowUp || (!l.ever_stayed && String(l.line_status || 'ACTIVE').toUpperCase() === 'ACTIVE'));
      // Store all lines for group check-in UI
      setCiLines(allLines.map((l) => {
        const rt = types.find((t) => String(t.room_type_id) === String(l.room_type_id));
        return {
          line_id: l.line_id,
          room_type_id: l.room_type_id,
          room_type_name: rt?.room_type_name || l.room_type_name || '',
          room_id: l.room_id ? String(l.room_id) : '',
          adults: l.adults || 1,
          children: l.children || 0,
          rate_amount: l.rate_amount || 0,
          tax_amount: l.tax_amount || 0
        };
      }));
      const firstLine = allLines[0];
      const anyNeedsRoom = allLines.some((l) => !l.room_id);
      setCiNeedsRoom(anyNeedsRoom);
      if (anyNeedsRoom && firstLine?.room_type_id) {
        setCiModal(prev => ({ ...prev, line_room_type_id: firstLine.room_type_id }));
      }
      // The booking-time advance is transferred to the folio by the backend
      // check-in itself, so it's shown for info only. deposit_amount is only
      // for money collected NOW — pre-filling it with netAdv posted the
      // booking advance a second time (as CASH, the default mode).
      setCiBookingAdvance(netAdv);
      setCiForm({
        checkin_datetime: toDateTimeLocalValue(now),
        checkout_datetime: `${depDate}T11:00`,
        deposit_amount: '',
        deposit_payment_mode: 'CASH',
        deposit_reference_no: '',
        deposit_remarks: '',
        room_id: firstLine?.room_id ? String(firstLine.room_id) : ''
      });
    } catch {
      setCiBookingAdvance(0);
      setCiForm({ checkin_datetime: toDateTimeLocalValue(now), checkout_datetime: `${depDate}T11:00`, deposit_amount: '', deposit_payment_mode: 'CASH', deposit_reference_no: '', deposit_remarks: '', room_id: '' });
    }
  };

  // Advance management helpers
  const openAdvanceModal = async (row, opts = {}) => {
    setAdvanceModal(row);
    setAdvanceError('');
    setAdvanceForm({ amount: '', payment_mode: 'CASH', reference_no: '', remarks: '' });
    setRefundForm({ amount: '', payment_mode: 'CASH', reference_no: '', remarks: '' });
    setShowRefundPanel(false);
    try {
      const res = await fetch(`/api/v1/pms/reservations/${row.reservation_id}/advances`, { headers: headers() });
      const data = res.ok ? await res.json() : { transactions: [], net_advance: 0 };
      setAdvances(data.transactions || []);
      setAdvancesVoided(data.voided || []);
      // Auto-expand refund panel when opened in refund context and advance exists
      if (opts.autoShowRefund && Number(data.net_advance || 0) > 0) {
        setShowRefundPanel(true);
      }
    } catch { setAdvances([]); }
  };

  const submitAdvance = async () => {
    if (!advanceModal?.reservation_id) return;
    if (!Number(advanceForm.amount) || Number(advanceForm.amount) <= 0) return setAdvanceError('Enter a valid amount');
    setAdvanceBusy(true); setAdvanceError('');
    try {
      const res = await fetch(`/api/v1/pms/reservations/${advanceModal.reservation_id}/advances`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ ...advanceForm, amount: Number(advanceForm.amount) })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to record advance');
      setAdvanceForm({ amount: '', payment_mode: 'CASH', reference_no: '', remarks: '' });
      // Reload advances
      const r2 = await fetch(`/api/v1/pms/reservations/${advanceModal.reservation_id}/advances`, { headers: headers() });
      const d2 = r2.ok ? await r2.json() : { transactions: [] };
      setAdvances(d2.transactions || []);
    } catch (err) { setAdvanceError(err.message); }
    finally { setAdvanceBusy(false); }
  };

  const openCorrectAdvance = (row) => {
    setCorrectAdvanceRow(row);
    setCorrectForm({ new_mode: row.payment_mode || '', reference_no: row.reference_no || '', reason: '' });
    setCorrectError('');
  };

  const submitAdvanceCorrection = async () => {
    if (!advanceModal?.reservation_id || !correctAdvanceRow) return;
    if (!correctForm.new_mode) return setCorrectError('Select the correct payment mode');
    if (!correctForm.reason.trim()) return setCorrectError('Please enter a reason for the correction');
    setCorrectBusy(true); setCorrectError('');
    try {
      const res = await fetch(`/api/v1/pms/reservations/${advanceModal.reservation_id}/advances/${correctAdvanceRow.advance_id}`, {
        method: 'PATCH', headers: headers(),
        body: JSON.stringify({ new_mode: correctForm.new_mode, reference_no: correctForm.reference_no.trim(), reason: correctForm.reason.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to correct payment mode');
      setCorrectAdvanceRow(null);
      const r2 = await fetch(`/api/v1/pms/reservations/${advanceModal.reservation_id}/advances`, { headers: headers() });
      const d2 = r2.ok ? await r2.json() : { transactions: [] };
      setAdvances(d2.transactions || []);
      if (data.warning) window.alert(data.warning);
    } catch (err) { setCorrectError(err.message); }
    finally { setCorrectBusy(false); }
  };

  const runSaveOnce = async (fn) => {
    if (savingReservationRef.current) return;
    savingReservationRef.current = true;
    setSavingReservation(true);
    try { await fn(); } finally {
      savingReservationRef.current = false;
      setSavingReservation(false);
    }
  };
  const createReservationDirect = () => runSaveOnce(createReservationDirectInner);
  const submit = () => runSaveOnce(submitInner);

  // Advance Void — Hotel Admin / Super Admin any advance; Front Office only
  // advances posted today. The API enforces the same rule and blocks it
  // after check-in (the advance is on the folio by then).
  const canVoidAnyAdvance = ['admin', 'hotel_admin', 'hotel admin'].includes(String(role || '').toLowerCase()) || Boolean(permissions?.hotel_admin);
  const canVoidAdvance = (t) => t?.txn_type === 'ADVANCE'
    && (canVoidAnyAdvance || (t.txn_date && new Date(t.txn_date).toDateString() === new Date().toDateString()));
  const voidAdvanceTxn = async (reservationId, t) => {
    const reason = window.prompt(`Void this advance?\n\n  ₹${Number(t.amount || 0).toFixed(2)} ${t.payment_mode || ''}  ${[t.reference_no, t.remarks].filter(Boolean).join(' — ')}\n\nThe entry is removed from the reservation and kept in the void log.\n\nEnter reason (required, min 3 chars):`);
    if (reason === null) return false;
    if (reason.trim().length < 3) { window.alert('Reason must be at least 3 characters.'); return false; }
    try {
      const res = await fetch(`/api/v1/pms/reservations/${reservationId}/advances/${t.advance_id}/void`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ reason: reason.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Could not void the advance');
      const r2 = await fetch(`/api/v1/pms/reservations/${reservationId}/advances`, { headers: headers() });
      const d2 = r2.ok ? await r2.json() : null;
      if (d2) {
        if (String(editingId) === String(reservationId)) {
          setEditingAdvanceSummary({ net_advance: Number(d2.net_advance || 0), transactions: d2.transactions || [], voided: d2.voided || [] });
        }
        if (String(advanceModal?.reservation_id) === String(reservationId)) {
          setAdvances(d2.transactions || []);
          setAdvancesVoided(d2.voided || []);
        }
      }
      return true;
    } catch (err) {
      window.alert(err.message);
      return false;
    }
  };

  const submitRefund = async () => {
    if (!advanceModal?.reservation_id) return;
    if (!Number(refundForm.amount) || Number(refundForm.amount) <= 0) return setAdvanceError('Enter a valid refund amount');
    setAdvanceBusy(true); setAdvanceError('');
    try {
      const res = await fetch(`/api/v1/pms/reservations/${advanceModal.reservation_id}/refund`, {
        method: 'POST', headers: headers(),
        body: JSON.stringify({ ...refundForm, amount: Number(refundForm.amount) })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Failed to record refund');
      setRefundForm({ amount: '', payment_mode: 'CASH', reference_no: '', remarks: '' });
      setShowRefundPanel(false);
      const r2 = await fetch(`/api/v1/pms/reservations/${advanceModal.reservation_id}/advances`, { headers: headers() });
      const d2 = r2.ok ? await r2.json() : { transactions: [] };
      setAdvances(d2.transactions || []);
    } catch (err) { setAdvanceError(err.message); }
    finally { setAdvanceBusy(false); }
  };

  // Numeric tail of a room number ("205" -> 205, "12A" -> 12) for proximity
  // scoring below. Mirrors backend/pmsRoomAssignUtils.js roomNumberDigits —
  // this is the manual-check-in counterpart of the same "keep multi-room
  // bookings together" heuristic OTA auto-assign uses.
  const roomNumberDigits = (roomNumber) => {
    const m = String(roomNumber || '').match(/\d+/);
    return m ? parseInt(m[0], 10) : null;
  };

  const scoreRoomCandidate = (candidate, siblingRooms) => {
    if (!siblingRooms.length) return 0;
    let best = -1;
    const candDigits = roomNumberDigits(candidate.room_number);
    for (const sib of siblingRooms) {
      let score = 0;
      if (candidate.block_name && sib.block_name && candidate.block_name === sib.block_name) score += 200;
      if (candidate.floor_no && sib.floor_no && candidate.floor_no === sib.floor_no) score += 100;
      const sibDigits = roomNumberDigits(sib.room_number);
      if (candDigits != null && sibDigits != null) score += Math.max(0, 50 - Math.abs(candDigits - sibDigits));
      if (score > best) best = score;
    }
    return Math.max(best, 0);
  };

  // Fills any still-empty lines with the best-scoring vacant room of the
  // matching type, preferring rooms on the same floor/block as a room
  // already assigned to this reservation (manual picks included). Never
  // overwrites a line the user already assigned — purely additive, and
  // everything it picks still goes through the same server-side
  // validation (occupancy re-check, block check) as a manual pick would.
  const autoAssignRooms = () => {
    setCiLines((prev) => {
      const usedIds = new Set(prev.filter((l) => l.room_id).map((l) => String(l.room_id)));
      const claimed = prev
        .filter((l) => l.room_id)
        .map((l) => rooms.find((r) => String(r.room_id) === String(l.room_id)))
        .filter(Boolean);
      return prev.map((line) => {
        if (line.room_id) return line;
        const candidates = rooms.filter((r) => r.is_active
          && String(r.occupancy_status || '').toUpperCase() === 'VACANT'
          && !['BLOCKED', 'OUT_OF_ORDER', 'PAINTING'].includes(String(r.maintenance_status || '').toUpperCase())
          && String(r.room_type_id) === String(line.room_type_id)
          && !usedIds.has(String(r.room_id)));
        if (!candidates.length) return line;
        const ranked = candidates
          .map((c) => ({ c, score: scoreRoomCandidate(c, claimed) }))
          .sort((a, b) => b.score - a.score
            || String(a.c.room_number).localeCompare(String(b.c.room_number), undefined, { numeric: true }));
        const best = ranked[0].c;
        usedIds.add(String(best.room_id));
        claimed.push(best);
        return { ...line, room_id: String(best.room_id) };
      });
    });
  };

  const submitCi = async () => {
    if (!ciModal?.reservation_id) return;
    setCiBusy(true);
    setCiError('');
    setCiResult(null);
    try {
      const payload = {};
      if (ciForm.checkin_datetime) payload.checkin_datetime = dateTimeLocalToIso(ciForm.checkin_datetime);
      if (ciForm.checkout_datetime) payload.checkout_datetime = dateTimeLocalToIso(ciForm.checkout_datetime);
      if (Number(ciForm.deposit_amount) > 0) {
        payload.deposit_amount = Number(ciForm.deposit_amount);
        payload.deposit_payment_mode = ciForm.deposit_payment_mode || 'CASH';
        payload.deposit_reference_no = ciForm.deposit_reference_no || '';
        payload.deposit_remarks = ciForm.deposit_remarks || 'Advance at check-in';
      }
      // Group check-in: send room_assignments for all lines
      if (ciLines.length > 1) {
        const missing = ciLines.find((l) => !l.room_id);
        if (missing) {
          setCiError('Please assign a room to every line before check-in.');
          setCiBusy(false);
          return;
        }
        const usedRooms = ciLines.map((l) => String(l.room_id));
        if (new Set(usedRooms).size !== usedRooms.length) {
          setCiError('Each line must have a different room assigned.');
          setCiBusy(false);
          return;
        }
        payload.room_assignments = ciLines.map((l) => ({ line_id: l.line_id, room_id: Number(l.room_id) }));
      } else if (ciLines.length === 1) {
        // Single line — use room_id from ciLines (may have been re-assigned)
        const rid = ciLines[0].room_id || ciForm.room_id;
        if (!rid) {
          setCiError('Please select a room to check in.');
          setCiBusy(false);
          return;
        }
        payload.room_assignments = [{ line_id: ciLines[0].line_id, room_id: Number(rid) }];
      } else {
        // Fallback: legacy single room_id
        if (ciForm.room_id) payload.room_id = Number(ciForm.room_id);
        if (ciNeedsRoom && !payload.room_id) {
          setCiError('Please select a room to check in.');
          setCiBusy(false);
          return;
        }
      }
      const res = await fetch(`/api/v1/pms/reservations/${ciModal.reservation_id}/checkin`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Check-in failed');
      setCiResult(data);
      // Open lock-encode modal for the primary room of this check-in.
      const primaryRoom = ciModal?.room_no || ciLines?.[0]?.room_no || ciForm?.room_id;
      if (ciModal?.reservation_id) {
        setLockEncodeModal({
          reservationId: ciModal.reservation_id,
          roomNumber: primaryRoom ? String(primaryRoom) : '',
          guestName: ciModal?.guest_name || ''
        });
      }
      loadData();
    } catch (err) {
      setCiError(err?.message || 'Check-in failed');
    } finally {
      setCiBusy(false);
    }
  };

  useEffect(() => {
    // OTA / Channel-Manager bookings carry an OTA-negotiated rate (e.g. Booking.com
    // net rate) that is NOT the rate-plan rate. loadReservationForEdit() sets the
    // form dates, which fires this effect — and a rate-plan re-quote here silently
    // overwrote the OTA rate (e.g. ₹9350 → the rate-plan ₹8400), so the folio no
    // longer matched the amount to collect and checkout showed a bogus refund.
    // The OTA rate is authoritative; never auto-re-quote a channel booking.
    if (editingOtaInfo) return;
    // Same class of bug as the OTA guard above, for every other booking source:
    // loadReservationForEdit() just set arrival_date/departure_date as part of
    // populating the whole form, which trips this effect even though the user
    // never touched the dates — re-quoting here would silently overwrite any
    // manually customized rate on the loaded lines with the rate-plan default.
    if (skipNextRequoteRef.current) {
      skipNextRequoteRef.current = false;
      return;
    }
    form.lines.forEach((_, idx) => {
      deriveQuoteForLine(idx, form);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.arrival_date, form.departure_date]);

  return (
    <div
      className="page-shell"
      ref={editFormRef}
      style={{
        transition: 'box-shadow 0.3s ease, outline-color 0.3s ease',
        outline: editHighlight ? '2px solid var(--theme-primary)' : '2px solid transparent',
        outlineOffset: 4,
        boxShadow: editHighlight ? '0 0 0 4px rgba(37, 99, 235, 0.15)' : 'none',
        borderRadius: 8,
      }}
    >
      <h3 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>
        Front Office - Reservations {editingId ? `(Edit #${editingId})` : ''}
        {editHighlight && <span style={{ marginLeft: 10, fontSize: '0.8rem', color: 'var(--theme-primary)', fontWeight: 600 }}>✎ Editing — update the details below</span>}
      </h3>

      {/* Step Indicator */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: '0.8rem' }}>
        {[{ n: 1, label: 'Reservation Details' }, { n: 2, label: 'ID Compliance' }].map((s, i) => (
          <React.Fragment key={s.n}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center',
                justifyContent: 'center', fontSize: 13, fontWeight: 700,
                background: step === s.n ? '#2563eb' : step > s.n ? 'var(--theme-success)' : '#e5e7eb',
                color: step >= s.n ? '#fff' : '#6b7280' }}>{step > s.n ? '✓' : s.n}</div>
              <span style={{ fontSize: 12, fontWeight: step === s.n ? 700 : 400,
                color: step === s.n ? '#2563eb' : '#6b7280' }}>{s.label}</span>
            </div>
            {i < 1 && <div style={{ flex: 1, height: 2, background: step > 1 ? 'var(--theme-success)' : '#e5e7eb',
              margin: '0 8px', minWidth: 30 }} />}
          </React.Fragment>
        ))}
      </div>

      {error ? <div className="error-banner">{error}</div> : null}
      {message ? <div className="success-banner">{message}</div> : null}

      {/* OTA/Channel Manager info banner */}
      {editingOtaInfo && (
        <div style={{ marginBottom: '0.6rem', padding: '0.5rem 0.75rem', background: '#f5f3ff', border: '1px solid #7c3aed', borderRadius: '6px', fontSize: '0.82rem', color: '#3b0764' }}>
          <strong style={{ color: '#7c3aed' }}>OTA Booking</strong>
          {' — '}Channel Manager Ref: <strong>{editingOtaInfo.channel_booking_id}</strong>
          {' | '}Source: <strong>{editingOtaInfo.booking_source}</strong>
          {' | '}Net Rate: <strong>₹{Number(editingOtaInfo.ota_net_rate).toFixed(2)}</strong>
          {' | '}Tax: <strong>₹{Number(editingOtaInfo.ota_tax_amount).toFixed(2)}</strong>
          {' | '}Booking Amount: <strong>₹{Number(editingOtaInfo.ota_booking_amount).toFixed(2)}</strong>
          {' | '}
          {editingOtaInfo.pay_at_hotel
            ? <span style={{ background: 'var(--theme-warning)', color: '#fff', borderRadius: '4px', padding: '1px 6px', fontWeight: 700 }}>Pay at Hotel — Full amount to collect</span>
            : <span style={{ background: 'var(--theme-success)', color: '#fff', borderRadius: '4px', padding: '1px 6px', fontWeight: 700 }}>Pre-paid by OTA — ₹0 to collect</span>
          }
        </div>
      )}

      {step === 1 && (
        <>
          <div
            style={{
              marginBottom: '0.55rem',
              fontSize: '0.82rem',
              border: `1px solid ${roomTaxPolicyConfigured ? '#9ae6b4' : '#fbd38d'}`,
              background: roomTaxPolicyConfigured ? '#f0fff4' : '#fffaf0',
              color: roomTaxPolicyConfigured ? '#22543d' : '#7b341e',
              borderRadius: '6px',
              padding: '0.4rem 0.55rem'
            }}
          >
            Tax Source: {roomTaxPolicyConfigured ? 'Hotel Room Tax Policy' : 'Rate Plan Tax Profile'}.
          </div>

          <div className="filter-bar" style={{ marginBottom: '0.8rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(160px,1fr))', gap: '0.5rem', marginBottom: '0.6rem' }}>
          <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {statusOptions(editingId ? form.status : null)}
          </select>
          <input
            type="date"
            value={form.arrival_date}
            min={!editingId ? toIsoDate(new Date()) : undefined}
            onChange={(e) => {
              const nextArrival = e.target.value;
              const minDeparture = addDaysIso(nextArrival, 1);
              const currentDeparture = form.departure_date || '';
              const nextDeparture = !currentDeparture || currentDeparture < minDeparture ? minDeparture : currentDeparture;
              setForm((prev) => ({
                ...prev,
                arrival_date: nextArrival,
                departure_date: nextDeparture,
                lines: prev.lines.map((l) => ({ ...l, arrival_date: nextArrival, departure_date: nextDeparture }))
              }));
            }}
          />
          <input
            type="date"
            value={form.departure_date}
            min={addDaysIso(form.arrival_date, 1)}
            onChange={(e) => {
              const nextDeparture = e.target.value;
              setForm((prev) => ({
                ...prev,
                departure_date: nextDeparture,
                lines: prev.lines.map((l) => ({ ...l, departure_date: nextDeparture }))
              }));
            }}
          />
          <input placeholder="Primary Guest Name" value={form.primary_guest_name} onChange={(e) => { setError(''); setForm({ ...form, primary_guest_name: e.target.value }); }} />
          <input
            placeholder="Mobile (10 digits)"
            value={form.mobile_number}
            maxLength={10}
            inputMode="numeric"
            onChange={(e) => {
              const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
              setError('');
              setForm({ ...form, mobile_number: digits });
            }}
          />
          <input type="email" placeholder="Email" value={form.email} onChange={(e) => { setError(''); setForm({ ...form, email: e.target.value }); }} />
          <input placeholder="Customer GSTIN (optional)" value={form.gst_number || ''} onChange={(e) => setForm({ ...form, gst_number: e.target.value.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 15) })} style={{ fontFamily: 'monospace' }} />
          <input placeholder="Company Name (optional)" value={form.company_name || ''} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
          {/* Per-booking billing address for the invoice -- separate from the
              guest's own "Permanent Address" on the ID Compliance step (that
              one is for police-report/compliance purposes) and separate from
              the Corporate's own billing address on Corporate Master (a
              master-data field, not something front desk edits per booking).
              Stored on pms_guest_master.billing_address alongside company_name/
              gst_number, same COALESCE-on-duplicate-guest treatment. */}
          <input placeholder="Billing Address (optional)" value={form.billing_address || ''} onChange={(e) => setForm({ ...form, billing_address: e.target.value })} />
          <select
            value={normalizeBookingSource(form.booking_source)}
            onChange={(e) => {
              const source = normalizeBookingSource(e.target.value);
              // The Sales Team member only applies to the Sales Team source.
              const salesMemberId = source === 'SALES_TEAM' ? form.sales_member_id : '';
              if (source === 'CORPORATE') {
                setForm((prev) => ({
                  ...prev,
                  booking_source: 'CORPORATE',
                  travel_agent_id: '',
                  travel_agent_commission_percent: 0,
                  travel_agent_commission_amount: 0,
                  sales_member_id: salesMemberId,
                  bill_to: prev.bill_to === 'SELF' ? 'CORPORATE' : prev.bill_to
                }));
              } else if (source === 'TRAVEL_AGENT') {
                setForm((prev) => ({
                  ...prev,
                  booking_source: 'TRAVEL_AGENT',
                  corporate_id: '',
                  sales_member_id: salesMemberId,
                  bill_to: 'SELF'
                }));
              } else {
                setForm((prev) => ({
                  ...prev,
                  booking_source: source,
                  corporate_id: '',
                  travel_agent_id: '',
                  travel_agent_commission_percent: 0,
                  travel_agent_commission_amount: 0,
                  sales_member_id: salesMemberId,
                  bill_to: 'SELF'
                }));
              }
            }}
          >
            <option value="DIRECT">Direct</option>
            <option value="PHONE">Phone</option>
            <option value="EMAIL">Email</option>
            <option value="WEBSITE">Website</option>
            <option value="WALK_IN">Walk-In</option>
            <option value="OTA">OTA (Other)</option>
            <option value="GOIBIBO">Goibibo</option>
            <option value="BOOKING.COM">Booking.com</option>
            <option value="AGODA">Agoda</option>
            <option value="MAKEMYTRIP">MakeMyTrip</option>
            <option value="CLEARTRIP">Cleartrip</option>
            <option value="CORPORATE">Corporate</option>
            <option value="TRAVEL_AGENT">Travel Agent</option>
            <option value="SALES_TEAM">Sales Team</option>
            <option value="REFERRAL">Referral</option>
            <option value="OTHER">Other</option>
          </select>
          {['CORPORATE', 'TRAVEL_AGENT'].includes(normalizeBookingSource(form.booking_source)) && (
            <select
              value={normalizeBookingSource(form.booking_source) === 'CORPORATE' ? form.corporate_id : form.travel_agent_id}
              onChange={(e) => {
                const source = normalizeBookingSource(form.booking_source);
                if (source === 'CORPORATE') {
                  // Company C/O has a corporate-level default (unlike Employee
                  // ID/Vessel Name, which have none) — prefill it from the
                  // corporate's own saved value so staff aren't forced to
                  // retype the common case, but never clobber a value already
                  // typed in or loaded for this reservation.
                  const pickedCorp = corporates.find((c) => String(c.corporate_id) === String(e.target.value));
                  setForm((prev) => ({
                    ...prev,
                    corporate_id: e.target.value,
                    bill_to: e.target.value ? (prev.bill_to === 'SELF' ? 'CORPORATE' : prev.bill_to) : 'SELF',
                    care_of: prev.care_of ? prev.care_of : (pickedCorp?.care_of || prev.care_of)
                  }));
                  return;
                }
                if (source === 'TRAVEL_AGENT') {
                  if (e.target.value === '__OTHERS__') {
                    setForm((prev) => ({
                      ...prev,
                      travel_agent_id: '__OTHERS__',
                      corporate_id: '',
                      bill_to: 'SELF',
                      travel_agent_commission_percent: 0,
                      travel_agent_commission_amount: 0
                    }));
                    setAdhocAgentName('');
                    setAdhocAgentCommission('');
                    return;
                  }
                  const picked = travelAgents.find((a) => String(a.travel_agent_id) === String(e.target.value));
                  setAdhocAgentName('');
                  setAdhocAgentCommission('');
                  setForm((prev) => {
                    const next = {
                      ...prev,
                      travel_agent_id: e.target.value,
                      corporate_id: '',
                      bill_to: 'SELF',
                      travel_agent_commission_percent: Number(picked?.commission_percent || 0)
                    };
                    return { ...next, travel_agent_commission_amount: computeCommissionAmount(next) };
                  });
                }
              }}
            >
              {normalizeBookingSource(form.booking_source) === 'CORPORATE' ? (
                <>
                  <option value="">Select Corporate</option>
                  {corporates.map((c) => (
                    <option key={c.corporate_id} value={c.corporate_id}>
                      {c.corporate_code} - {c.corporate_name}
                    </option>
                  ))}
                </>
              ) : (
                <>
                  <option value="">Select Travel Agent</option>
                  {travelAgents.map((a) => (
                    <option key={a.travel_agent_id} value={a.travel_agent_id}>
                      {a.agent_code} - {a.agent_name}
                    </option>
                  ))}
                  <option value="__OTHERS__">Others (Ad-hoc)</option>
                </>
              )}
            </select>
          )}
          {normalizeBookingSource(form.booking_source) === 'TRAVEL_AGENT' && form.travel_agent_id === '__OTHERS__' && (
            <>
              <input
                value={adhocAgentName}
                onChange={(e) => setAdhocAgentName(e.target.value)}
                placeholder="Travel Agent Name *"
                style={{ borderColor: !adhocAgentName.trim() ? '#fca5a5' : undefined }}
              />
              <input
                type="number"
                min={0}
                step="0.01"
                value={adhocAgentCommission}
                onChange={(e) => {
                  setAdhocAgentCommission(e.target.value);
                  const pct = Number(e.target.value || 0);
                  setForm((prev) => {
                    const next = { ...prev, travel_agent_commission_percent: pct };
                    return { ...next, travel_agent_commission_amount: computeCommissionAmount(next) };
                  });
                }}
                placeholder="Commission %"
              />
            </>
          )}
          {/* Booking Source = Sales Team → pick the member (Front Office > Masters > Sales Team).
              Inactive members are hidden unless already assigned to this booking. */}
          {normalizeBookingSource(form.booking_source) === 'SALES_TEAM' && (
            <select
              title="Sales Team Member"
              value={form.sales_member_id || ''}
              onChange={(e) => setForm((prev) => ({ ...prev, sales_member_id: e.target.value }))}
            >
              <option value="">Select Sales Team Member</option>
              {salesTeam
                .filter((m) => m.is_active || String(m.member_id) === String(form.sales_member_id))
                .map((m) => (
                  <option key={m.member_id} value={m.member_id}>
                    {m.member_name}{m.employee_code ? ` (${m.employee_code})` : ''}{m.is_active ? '' : ' — inactive'}
                  </option>
                ))}
            </select>
          )}
          <select value={form.bill_to} onChange={(e) => setForm({ ...form, bill_to: e.target.value })} disabled={normalizeBookingSource(form.booking_source) !== 'CORPORATE' || !form.corporate_id}>
            <option value="SELF">SELF</option>
            <option value="CORPORATE">CORPORATE</option>
            <option value="SPLIT">SPLIT</option>
          </select>
          {/* REQ-15 (docs/SRS_AMBERPARAMOUNT_REQ08-15.md) — only asked for when
              the selected corporate has that field enabled on its invoices. */}
          {form.bill_to === 'CORPORATE' && form.corporate_id && (() => {
            const selectedCorp = corporates.find((c) => String(c.corporate_id) === String(form.corporate_id));
            if (!selectedCorp) return null;
            return (
              <>
                {selectedCorp.invoice_show_employee_id && (
                  <input
                    placeholder="Employee ID"
                    value={form.employee_id}
                    onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                  />
                )}
                {selectedCorp.invoice_show_vessel_name && (
                  <input
                    placeholder="Vessel Name(s)"
                    value={form.vessel_name}
                    onChange={(e) => setForm({ ...form, vessel_name: e.target.value })}
                  />
                )}
                {selectedCorp.invoice_show_care_of && (
                  <input
                    placeholder="Company C/O"
                    value={form.care_of}
                    onChange={(e) => setForm({ ...form, care_of: e.target.value })}
                  />
                )}
              </>
            );
          })()}
          {normalizeBookingSource(form.booking_source) === 'TRAVEL_AGENT' && form.travel_agent_id !== '__OTHERS__' ? (
            <input
              type="number"
              min={0}
              step="0.001"
              placeholder="Travel Agent Commission %"
              value={form.travel_agent_commission_percent}
              onChange={(e) => {
                const pct = Number(e.target.value || 0);
                setForm((prev) => {
                  const next = { ...prev, travel_agent_commission_percent: pct };
                  return { ...next, travel_agent_commission_amount: computeCommissionAmount(next) };
                });
              }}
            />
          ) : null}
          {normalizeBookingSource(form.booking_source) === 'TRAVEL_AGENT' ? (
            <input type="number" min={0} placeholder="Travel Agent Commission Amount" value={form.travel_agent_commission_amount} readOnly />
          ) : null}
          {normalizeBookingSource(form.booking_source) === 'CORPORATE' && form.bill_to === 'SPLIT' ? (
            <input
              type="number"
              min={0}
              placeholder="Corporate Hold Amount"
              value={form.credit_hold_amount}
              onChange={(e) => setForm({ ...form, credit_hold_amount: e.target.value })}
            />
          ) : null}
        </div>

        {/* Quick Add Rooms Panel */}
        <div style={{ marginBottom: '0.5rem' }}>
          <button
            type="button"
            onClick={() => setQuickAddOpen((v) => !v)}
            style={{ background: '#eef2ff', color: '#3730a3', border: '1px solid #c7d2fe', borderRadius: 6,
              padding: '5px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            {quickAddOpen ? '− Quick Add Rooms' : '+ Quick Add Rooms'}
          </button>
          {quickAddOpen && (
            <div style={{ border: '1px solid #c7d2fe', borderRadius: 8, padding: '0.7rem 1rem', marginTop: '0.4rem',
              background: '#f5f7ff', display: isMobile ? 'grid' : 'flex', gridTemplateColumns: isMobile ? '1fr 1fr' : undefined, flexWrap: 'wrap', gap: '0.6rem', alignItems: 'flex-end' }}>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600, color: 'var(--theme-text)', gridColumn: isMobile ? '1 / -1' : undefined }}>
                Room Type
                <select value={quickAdd.room_type_id}
                  onChange={(e) => {
                    const rtId = e.target.value;
                    const rt = types.find((t) => String(t.room_type_id) === String(rtId));
                    setQuickAdd((p) => ({
                      ...p,
                      room_type_id: rtId,
                      rate_plan_code: '',
                      adults: Number(p.count || 1) * Math.min(2, rt ? Math.max(1, Number(rt.max_adults || 2)) : 2),
                      children: 0
                    }));
                  }}
                  style={{ marginTop: 2, minWidth: isMobile ? 0 : 140, width: isMobile ? '100%' : undefined }}>
                  <option value="">Select</option>
                  {types.map((t) => <option key={t.room_type_id} value={t.room_type_id}>{t.room_type_name}</option>)}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600, color: 'var(--theme-text)' }}>
                No. of Rooms
                <input type="number" min={1} max={50} value={quickAdd.count}
                  onChange={(e) => setQuickAdd((p) => ({ ...p, count: e.target.value }))}
                  style={{ marginTop: 2, width: isMobile ? '100%' : 60 }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600, color: 'var(--theme-text)' }}>
                Total Adults
                <input type="number" min={0} max={999} value={quickAdd.adults}
                  onKeyDown={(e) => e.key === '-' && e.preventDefault()}
                  onChange={(e) => { const n = parseInt(e.target.value, 10); setQuickAdd((p) => ({ ...p, adults: isNaN(n) ? 0 : Math.max(0, n) })); }}
                  style={{ marginTop: 2, width: isMobile ? '100%' : 65 }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600, color: 'var(--theme-text)' }}>
                Total Children
                <input type="number" min={0} max={999} value={quickAdd.children}
                  onKeyDown={(e) => e.key === '-' && e.preventDefault()}
                  onChange={(e) => { const n = parseInt(e.target.value, 10); setQuickAdd((p) => ({ ...p, children: isNaN(n) ? 0 : Math.max(0, n) })); }}
                  style={{ marginTop: 2, width: isMobile ? '100%' : 65 }} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600, color: 'var(--theme-text)', gridColumn: isMobile ? '1 / -1' : undefined }}>
                Rate Plan
                <select value={quickAdd.rate_plan_code}
                  onChange={(e) => setQuickAdd((p) => ({ ...p, rate_plan_code: e.target.value }))}
                  style={{ marginTop: 2, minWidth: isMobile ? 0 : 130, width: isMobile ? '100%' : undefined }}>
                  {!quickAdd.rate_plan_code && <option value="">-- Select Rate Plan --</option>}
                  {ratePlans
                    .filter((rp) => !quickAdd.room_type_id || String(rp.room_type_id || '') === String(quickAdd.room_type_id))
                    .map((rp) => (
                      <option key={rp.rate_plan_id} value={rp.rate_plan_code}>{rp.rate_plan_code} - {rp.rate_plan_name}</option>
                    ))}
                </select>
              </label>
              <button
                type="button"
                disabled={!quickAdd.room_type_id || quickAdd.count < 1}
                onClick={addQuickRooms}
                style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6,
                  padding: '7px 18px', fontWeight: 600, cursor: 'pointer', opacity: (!quickAdd.room_type_id || quickAdd.count < 1) ? 0.5 : 1 }}
              >
                Add {Math.min(Math.max(1, Number(quickAdd.count) || 1), 50)} Room{Number(quickAdd.count) !== 1 ? 's' : ''}
              </button>
            </div>
          )}
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: '#2b6cb0', color: '#fff' }}>
                <th style={{ padding: '0.4rem' }}>Room Type</th>
                <th style={{ padding: '0.4rem' }}>Room</th>
                <th style={{ padding: '0.4rem' }}>Adults</th>
                <th style={{ padding: '0.4rem' }}>Children</th>
                <th style={{ padding: '0.4rem' }}>Rate Plan</th>
                <th style={{ padding: '0.4rem' }} title="Include: Rate / Night already contains GST. Exclude: GST is added on top.">GST Incl / Excl</th>
                <th style={{ padding: '0.4rem', textAlign: 'right' }}>Rate / Night</th>
                <th style={{ padding: '0.4rem', textAlign: 'right' }}>Tax / Night</th>
                <th style={{ padding: '0.4rem' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {form.lines.map((line, idx) => {
                const nights = nightsBetween(
                  line.arrival_date || form.arrival_date,
                  line.departure_date || form.departure_date
                );
                const linePlan = ratePlans.find((rp) => String(rp.rate_plan_code) === String(line.rate_plan_code));
                const lineTaxMode = line.tax_mode || (linePlan?.tax_inclusive ? 'INCLUSIVE' : 'EXCLUSIVE');
                const isInclusive = lineTaxMode === 'INCLUSIVE';
                // Inclusive: Rate / Night shows the GST-inclusive amount the guest
                // pays (net + tax) — that is also what an entered override means.
                const perNight = (amt) => (nights > 0 ? Number(amt || 0) / nights : Number(amt || 0));
                const taxPerNight  = perNight(line.tax_amount);
                const ratePerNight = perNight(line.rate_amount) + (isInclusive ? taxPerNight : 0);
                const origRatePerNight = perNight(line._orig_rate_amount ?? line.rate_amount)
                  + (isInclusive ? perNight(line._orig_tax_amount ?? line.tax_amount) : 0);
                return (
                  <React.Fragment key={`line-${idx}`}>
                  <tr style={{ borderBottom: line.rate_quote_error ? 'none' : '1px solid var(--theme-border)' }}>
                    <td style={{ padding: '0.4rem' }}>
                      <select value={line.room_type_id} onChange={(e) => updateLine(idx, { room_type_id: e.target.value })}>
                        <option value="">Select</option>
                        {types.map((t) => <option key={t.room_type_id} value={t.room_type_id}>{t.room_type_name}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '0.4rem' }}>
                      <select value={line.room_id} onChange={(e) => updateLine(idx, { room_id: e.target.value })}>
                        <option value="">Unassigned</option>
                        {rooms
                          .filter((r) => (!line.room_type_id || String(r.room_type_id) === String(line.room_type_id))
                            && (String(r.occupancy_status || '').toUpperCase() !== 'OCCUPIED' || String(r.room_id) === String(line.room_id))
                            && (!['BLOCKED', 'OUT_OF_ORDER', 'PAINTING'].includes(String(r.maintenance_status || '').toUpperCase()) || String(r.room_id) === String(line.room_id)))
                          .map((r) => <option key={r.room_id} value={r.room_id}>{r.room_number}{String(r.occupancy_status || '').toUpperCase() === 'OCCUPIED' ? ' (Occupied)' : ''}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '0.4rem' }}><input type="number" min={1} step={1} value={line.adults} onKeyDown={(e) => e.key === '-' && e.preventDefault()} onChange={(e) => updateLine(idx, { adults: e.target.value })} /></td>
                    <td style={{ padding: '0.4rem' }}><input type="number" min={0} step={1} value={line.children} onKeyDown={(e) => e.key === '-' && e.preventDefault()} onChange={(e) => updateLine(idx, { children: e.target.value })} /></td>
                    <td style={{ padding: '0.4rem' }}>
                      <select value={line.rate_plan_code || ''} onChange={(e) => updateLine(idx, { rate_plan_code: e.target.value })}>
                        {!line.rate_plan_code && <option value="">-- Select Rate Plan --</option>}
                        {ratePlans
                          .filter((rp) => !line.room_type_id || String(rp.room_type_id || '') === String(line.room_type_id))
                          .map((rp) => (
                          <option key={rp.rate_plan_id} value={rp.rate_plan_code}>{rp.rate_plan_code} - {rp.rate_plan_name}</option>
                          ))}
                      </select>
                    </td>
                    <td style={{ padding: '0.4rem' }}>
                      <select
                        value={lineTaxMode}
                        disabled={!line.rate_plan_code || Boolean(editingOtaInfo)}
                        title={editingOtaInfo ? 'OTA booking — tax as received from the channel' : undefined}
                        onChange={(e) => updateLine(idx, { tax_mode: e.target.value })}
                      >
                        <option value="EXCLUSIVE">Exclude</option>
                        <option value="INCLUSIVE">Include</option>
                      </select>
                    </td>
                    <td style={{ padding: '0.4rem', textAlign: 'right', fontWeight: 500 }}>
                      <input type="text" inputMode="decimal"
                        defaultValue={ratePerNight > 0 ? ratePerNight.toFixed(2) : ''}
                        key={`rate-${idx}-${line._orig_rate_amount || ''}-${lineTaxMode}`}
                        placeholder="0.00"
                        title={line.rate_quote_error || undefined}
                        style={{ width: 85, textAlign: 'right', padding: '0.2rem 0.35rem', border: line.rate_quote_error ? '1px solid #dc2626' : '1px solid #cbd5e0', borderRadius: 4, fontSize: '0.85rem', fontFamily: 'monospace' }}
                        onBlur={(e) => {
                          const val = e.target.value.replace(/[^0-9.]/g, '');
                          const newRatePerNight = Number(val || 0);
                          if (newRatePerNight === Number(origRatePerNight.toFixed(2))) return;
                          if (newRatePerNight > 0) {
                            // Persist the per-night intent on the line so later date
                            // changes rescale from it rather than reverting to default.
                            setForm((prev) => {
                              const lines = [...prev.lines];
                              if (lines[idx]) lines[idx] = { ...lines[idx], rate_override: true, rate_override_per_night: newRatePerNight };
                              return { ...prev, lines };
                            });
                            deriveQuoteForLine(idx, form, { overridePerNight: newRatePerNight });
                          } else {
                            setForm((prev) => {
                              const lines = [...prev.lines];
                              if (lines[idx]) lines[idx] = { ...lines[idx], rate_override: false, rate_override_per_night: undefined };
                              return { ...prev, lines };
                            });
                            deriveQuoteForLine(idx, form);
                          }
                        }}
                      />
                    </td>
                    <td style={{ padding: '0.4rem', textAlign: 'right', color: '#c05621' }}>
                      {taxPerNight > 0 ? taxPerNight.toFixed(2) : '-'}
                      {taxPerNight > 0 && isInclusive ? (
                        <div style={{ fontSize: '0.7rem', color: '#718096' }}>incl. in rate</div>
                      ) : null}
                    </td>
                    <td style={{ padding: '0.4rem' }}><button onClick={() => removeLine(idx)}>Remove</button></td>
                  </tr>
                  {line.rate_quote_error && (
                    <tr style={{ borderBottom: '1px solid var(--theme-border)' }}>
                      <td colSpan={9} style={{ padding: '0 0.4rem 0.5rem', color: '#dc2626', fontSize: '0.8rem' }}>
                        {line.rate_quote_error}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Remaining pax banner — shows when Quick Add pax > assigned pax */}
        {paxTarget.adults > 0 && (() => {
          const assignedAdults = form.lines.reduce((s, l) => s + Number(l.adults || 0), 0);
          const assignedChildren = form.lines.reduce((s, l) => s + Number(l.children || 0), 0);
          const remA = paxTarget.adults - assignedAdults;
          const remC = paxTarget.children - assignedChildren;
          if (remA <= 0 && remC <= 0) return null;
          return (
            <div style={{ marginTop: '0.5rem', padding: '0.45rem 0.7rem', background: '#fff7ed', border: '1px solid #fdba74',
              borderRadius: 6, fontSize: 12, color: '#92400e', fontWeight: 600 }}>
              {remA > 0 && <span>{remA} adult{remA !== 1 ? 's' : ''} unassigned (requested {paxTarget.adults}, placed {assignedAdults}). </span>}
              {remC > 0 && <span>{remC} child{remC !== 1 ? 'ren' : ''} unassigned (requested {paxTarget.children}, placed {assignedChildren}). </span>}
              <span>Add more rooms or adjust counts in existing rooms.</span>
            </div>
          );
        })()}

        {/* Booking Summary */}
        {(() => {
          const summaryLines = form.lines.map((line) => {
            const nights = nightsBetween(
              line.arrival_date || form.arrival_date,
              line.departure_date || form.departure_date
            );
            const roomCharges = Number(line.rate_amount || 0);
            const taxAmount   = Number(line.tax_amount  || 0);
            const lineTotal   = roomCharges + taxAmount;
            const ratePerNight = nights > 0 ? roomCharges / nights : roomCharges;
            return { nights, ratePerNight, roomCharges, taxAmount, lineTotal };
          });
          const totalRoomCharges = summaryLines.reduce((s, l) => s + l.roomCharges, 0);
          const totalTax         = summaryLines.reduce((s, l) => s + l.taxAmount,   0);
          const grandTotal       = totalRoomCharges + totalTax;
          const hasData = grandTotal > 0;
          if (!hasData) return null;
          return (
            <div style={{ marginTop: '0.7rem', background: '#ebf8ff', border: '1px solid #bee3f8', borderRadius: '7px', padding: '0.6rem 0.8rem' }}>
              <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#2b6cb0', marginBottom: '0.4rem' }}>Booking Summary</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', fontSize: '0.83rem', width: '100%' }}>
                  <thead>
                    <tr style={{ background: '#bee3f8' }}>
                      <th style={{ padding: '0.3rem 0.6rem', textAlign: 'left' }}>Line</th>
                      <th style={{ padding: '0.3rem 0.6rem', textAlign: 'right' }}>Nights</th>
                      <th style={{ padding: '0.3rem 0.6rem', textAlign: 'right' }}>Rate / Night</th>
                      <th style={{ padding: '0.3rem 0.6rem', textAlign: 'right' }}>Room Charges</th>
                      <th style={{ padding: '0.3rem 0.6rem', textAlign: 'right' }}>Tax</th>
                      <th style={{ padding: '0.3rem 0.6rem', textAlign: 'right' }}>Line Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryLines.map((sl, idx) => (
                      <tr key={idx} style={{ borderBottom: '1px solid #bee3f8' }}>
                        <td style={{ padding: '0.28rem 0.6rem', color: '#4a5568' }}>Room {idx + 1}</td>
                        <td style={{ padding: '0.28rem 0.6rem', textAlign: 'right' }}>{sl.nights}</td>
                        <td style={{ padding: '0.28rem 0.6rem', textAlign: 'right' }}>{sl.nights > 0 ? sl.ratePerNight.toFixed(2) : '-'}</td>
                        <td style={{ padding: '0.28rem 0.6rem', textAlign: 'right' }}>{sl.roomCharges.toFixed(2)}</td>
                        <td style={{ padding: '0.28rem 0.6rem', textAlign: 'right', color: '#c05621' }}>{sl.taxAmount.toFixed(2)}</td>
                        <td style={{ padding: '0.28rem 0.6rem', textAlign: 'right', fontWeight: 600 }}>{sl.lineTotal.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#2b6cb0', color: '#fff', fontWeight: 700 }}>
                      <td style={{ padding: '0.35rem 0.6rem' }} colSpan={3}>Total</td>
                      <td style={{ padding: '0.35rem 0.6rem', textAlign: 'right' }}>{totalRoomCharges.toFixed(2)}</td>
                      <td style={{ padding: '0.35rem 0.6rem', textAlign: 'right' }}>{totalTax.toFixed(2)}</td>
                      <td style={{ padding: '0.35rem 0.6rem', textAlign: 'right' }}>{grandTotal.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          );
        })()}

        {/* Advance Payment at Booking */}
        <div style={{ marginTop: '0.7rem', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '7px', padding: '0.6rem 0.8rem' }}>
          <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#166534', marginBottom: '0.4rem' }}>Advance Payment</div>

          {/* Existing advances (shown when editing) */}
          {editingId && editingAdvanceSummary && (editingAdvanceSummary.transactions?.length > 0 || editingAdvanceSummary.voided?.length > 0) && (
            <div style={{ marginBottom: '0.6rem' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem', marginBottom: '0.4rem' }}>
                <thead>
                  <tr style={{ background: '#1a365d' }}>
                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', fontWeight: 700, color: '#fff', fontSize: '0.8rem' }}>Date</th>
                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', fontWeight: 700, color: '#fff', fontSize: '0.8rem' }}>Type</th>
                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontWeight: 700, color: '#fff', fontSize: '0.8rem' }}>Amount</th>
                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', fontWeight: 700, color: '#fff', fontSize: '0.8rem' }}>Mode</th>
                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', fontWeight: 700, color: '#fff', fontSize: '0.8rem' }}>Ref / Remarks</th>
                    <th style={{ padding: '0.35rem 0.5rem', textAlign: 'center', fontWeight: 700, color: '#fff', fontSize: '0.8rem' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {editingAdvanceSummary.transactions.map((t, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #bbf7d0' }}>
                      <td style={{ padding: '0.25rem 0.5rem' }}>{t.txn_date ? new Date(t.txn_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</td>
                      <td style={{ padding: '0.25rem 0.5rem' }}>
                        <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: '0.72rem', fontWeight: 700, background: t.txn_type === 'ADVANCE' ? '#dcfce7' : '#fee2e2', color: t.txn_type === 'ADVANCE' ? '#166534' : '#991b1b' }}>{t.txn_type}</span>
                      </td>
                      <td style={{ padding: '0.25rem 0.5rem', textAlign: 'right', fontFamily: 'monospace', fontWeight: 600 }}>{Number(t.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td style={{ padding: '0.25rem 0.5rem' }}>{t.payment_mode || '-'}</td>
                      <td style={{ padding: '0.25rem 0.5rem', color: '#64748b' }}>{[t.reference_no, t.remarks].filter(Boolean).join(' — ') || '-'}</td>
                      <td style={{ padding: '0.25rem 0.5rem', textAlign: 'center' }}>
                        {canVoidAdvance(t) ? (
                          <button type="button" onClick={() => voidAdvanceTxn(editingId, t)} title="Void this advance (reason required, logged)" style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem', border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>Void</button>
                        ) : '-'}
                      </td>
                    </tr>
                  ))}
                  {(editingAdvanceSummary.voided || []).map((v) => (
                    <tr key={`void-${v.void_id}`} style={{ borderBottom: '1px solid #bbf7d0', color: '#9ca3af' }}>
                      <td style={{ padding: '0.25rem 0.5rem', textDecoration: 'line-through' }}>{v.txn_date ? new Date(v.txn_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}</td>
                      <td style={{ padding: '0.25rem 0.5rem' }}>
                        <span style={{ padding: '1px 6px', borderRadius: 3, fontSize: '0.72rem', fontWeight: 700, background: '#f3f4f6', color: '#6b7280' }}>VOIDED</span>
                      </td>
                      <td style={{ padding: '0.25rem 0.5rem', textAlign: 'right', fontFamily: 'monospace', textDecoration: 'line-through' }}>{Number(v.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td style={{ padding: '0.25rem 0.5rem', textDecoration: 'line-through' }}>{v.payment_mode || '-'}</td>
                      <td colSpan={2} style={{ padding: '0.25rem 0.5rem' }}>
                        Voided by {v.voided_by || '-'} on {v.voided_at ? new Date(v.voided_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'} — {v.void_reason}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ display: 'flex', gap: '1.2rem', fontWeight: 700, fontSize: '0.85rem', padding: '0.3rem 0.5rem', background: '#d1fae5', borderRadius: 4 }}>
                <span style={{ color: '#166534' }}>Net Advance Collected: <span style={{ fontFamily: 'monospace' }}>{'\u20B9'}{Number(editingAdvanceSummary.net_advance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span></span>
              </div>
            </div>
          )}
          {editingId && (!editingAdvanceSummary || (!editingAdvanceSummary.transactions?.length && !editingAdvanceSummary.voided?.length)) && (
            <div style={{ marginBottom: '0.5rem', fontSize: '0.82rem', color: '#6b7280', fontStyle: 'italic' }}>No advance collected yet for this reservation.</div>
          )}

          {/* Add new advance */}
          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#475569', marginBottom: '0.3rem' }}>{editingId ? 'Add More Advance' : 'Collect Advance (optional)'}</div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 1fr', gap: '0.5rem' }}>
            <label style={{ display: 'grid', gap: '0.2rem' }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 600 }}>Amount</span>
              <input type="text" inputMode="decimal" placeholder="0.00" value={form.advance_amount} onChange={(e) => setForm({ ...form, advance_amount: e.target.value })} style={{ padding: '0.3rem', borderRadius: 4, border: '1px solid #cbd5e0' }} />
            </label>
            <label style={{ display: 'grid', gap: '0.2rem' }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 600 }}>Payment Mode</span>
              <select value={form.advance_payment_mode} onChange={(e) => setForm({ ...form, advance_payment_mode: e.target.value })} style={{ padding: '0.3rem', borderRadius: 4, border: '1px solid #cbd5e0' }}>
                <option value="CASH">Cash</option>
                <option value="CARD">Card</option>
                <option value="UPI">UPI</option>
                <option value="BANK_TRANSFER">Bank Transfer</option>
                <option value="CHEQUE">Cheque</option>
                <option value="WALLET">Wallet</option>
              </select>
            </label>
            <label style={{ display: 'grid', gap: '0.2rem' }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 600 }}>Reference No</span>
              <input placeholder="Txn / Receipt No" value={form.advance_reference_no} onChange={(e) => setForm({ ...form, advance_reference_no: e.target.value })} style={{ padding: '0.3rem', borderRadius: 4, border: '1px solid #cbd5e0' }} />
            </label>
            <label style={{ display: 'grid', gap: '0.2rem' }}>
              <span style={{ fontSize: '0.78rem', fontWeight: 600 }}>Remarks</span>
              <input placeholder="Optional note" value={form.advance_remarks} onChange={(e) => setForm({ ...form, advance_remarks: e.target.value })} style={{ padding: '0.3rem', borderRadius: 4, border: '1px solid #cbd5e0' }} />
            </label>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <button onClick={addLine}>Add Line</button>
          <button onClick={createReservationDirect} disabled={savingReservation} style={{ background: 'var(--theme-success, #059669)', color: '#fff', border: 'none',
            borderRadius: 6, padding: '7px 20px', fontWeight: 700, cursor: savingReservation ? 'wait' : 'pointer', fontSize: '0.88rem', opacity: savingReservation ? 0.6 : 1 }}>
            {savingReservation ? 'Saving…' : (editingId ? 'Update Reservation' : 'Create Reservation')}
          </button>
          <button onClick={goToComplianceStep} style={{ background: '#2563eb', color: '#fff', border: 'none',
            borderRadius: 6, padding: '7px 20px', fontWeight: 600, cursor: 'pointer' }}>
            Next: ID Compliance →
          </button>
          <button onClick={() => { setForm(buildEmptyForm()); loadPropertyBusinessDate(); setEditingId(''); setEditingOtaInfo(null); setEditingAdvanceSummary(null); setStep(1); setPaxTarget({ adults: 0, children: 0 }); }}>Clear</button>
        </div>
          </div>
        </>
      )}

      {/* Step 2: Compliance */}
      {step === 2 && (
        <div style={{ background: '#f8faff', border: '1px solid #d7e3f4', borderRadius: 10, padding: '1rem', marginBottom: '0.8rem' }}>
          <div style={{ marginBottom: 12, fontSize: 13, color: 'var(--theme-text)' }}>
            Guest: <b>{form.primary_guest_name}</b> &nbsp;|&nbsp; Mobile: <b>{form.mobile_number}</b>
            &nbsp;|&nbsp; Status: <b>{form.status}</b>
            &nbsp;|&nbsp; Adults: <b>{form.lines.reduce((s, l) => s + Number(l.adults || 0), 0)}</b>
            {complianceGuestList.length > 1 && (
              <span style={{ marginLeft: 8, padding: '2px 8px', background: '#dcfce7', color: '#166534',
                borderRadius: 10, fontSize: 11, fontWeight: 600 }}>
                Primary guest ID required. Guest 2-{complianceGuestList.length} optional — can be added later.
              </span>
            )}
          </div>

          {/* Guest Tabs */}
          {complianceGuestList.length > 1 && (
            <div style={{ display: 'flex', gap: 4, marginBottom: 14, flexWrap: 'wrap' }}>
              {complianceGuestList.map((g, idx) => {
                const hasErr = idx === 0 && Object.keys(complianceErrorsList[idx] || {}).length > 0;
                const isActive = idx === activeGuestIdx;
                const isFilled = idx > 0 && g && (g.id_proof_no || g.id_proof_type || g.gender || g.dob);
                return (
                  <button key={idx} onClick={() => setActiveGuestIdx(idx)}
                    style={{
                      padding: '5px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      border: isActive ? '2px solid #2563eb' : '1px solid #d1d5db',
                      background: isActive ? '#eff6ff' : hasErr ? '#fef2f2' : isFilled ? '#f0fdf4' : '#fff',
                      color: isActive ? '#1e40af' : hasErr ? 'var(--theme-danger)' : isFilled ? '#166534' : 'var(--theme-text)'
                    }}>
                    {idx === 0 ? 'Guest 1 (Primary)' : `Guest ${idx + 1}`}
                    {idx > 0 && !isFilled && <span style={{ marginLeft: 4, fontSize: 10, color: '#9ca3af' }}>(Optional)</span>}
                    {isFilled && <span style={{ marginLeft: 4, color: 'var(--theme-success)' }}>&#10003;</span>}
                    {hasErr && <span style={{ marginLeft: 4, color: 'var(--theme-danger)' }}>&#9888;</span>}
                  </button>
                );
              })}
            </div>
          )}

          <PmsComplianceCheckin
            values={complianceGuestList[activeGuestIdx] || {}}
            onChange={(name, val) => setComplianceGuestList((prev) => {
              const next = [...prev];
              next[activeGuestIdx] = { ...next[activeGuestIdx], [name]: val };
              return next;
            })}
            fieldRules={fieldRules}
            errors={complianceErrorsList[activeGuestIdx] || {}}
            policyConfig={policyConfig}
            countryOptions={countryOptions}
            idTypeOptions={idTypeOptions}
            hotelCountry={selectedProperty?.country || 'India'}
            showGuestName={activeGuestIdx > 0}
          />

          {/* Guest Intelligence Card (AI) */}
          {aiGuestRecognitionEnabled && activeGuestIdx === 0 && form.mobile_number && (
            <GuestIntelligenceCard
              mobileNumber={form.mobile_number}
              chainId={selectedProperty?.chain_id}
              propertyId={selectedProperty?.id}
              token={token}
            />
          )}

          {/* Guest navigation */}
          {complianceGuestList.length > 1 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              {activeGuestIdx > 0 && (
                <button onClick={() => setActiveGuestIdx((i) => i - 1)}
                  style={{ padding: '5px 14px', background: '#e5e7eb', color: 'var(--theme-text)', border: 'none',
                    borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  ← Previous Guest
                </button>
              )}
              {activeGuestIdx < complianceGuestList.length - 1 && (
                <button onClick={() => setActiveGuestIdx((i) => i + 1)}
                  style={{ padding: '5px 14px', background: '#2563eb', color: '#fff', border: 'none',
                    borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Next Guest →
                </button>
              )}
            </div>
          )}

          {complianceErrorsList.reduce((s, e) => s + Object.keys(e).length, 0) > 0 && (() => {
            const missingCount = complianceErrorsList.reduce((s, e) => s + Object.keys(e).length, 0);
            return (
              <div style={{ marginTop: 14, padding: '10px 14px', background: '#fffbeb',
                border: '1px solid #fbbf24', borderRadius: 8 }}>
                <div style={{ fontSize: 13, color: '#b45309', fontWeight: 600, marginBottom: 6 }}>
                  {missingCount} required field{missingCount > 1 ? 's are' : ' is'} missing for primary guest.
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                  <input type='checkbox' checked={overrideMode}
                    onChange={(e) => { setOverrideMode(e.target.checked); setOverrideReason(''); }} />
                  Override with reason (for TENTATIVE reservations)
                </label>
                {overrideMode && (
                  <textarea value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)}
                    placeholder='Enter override reason (required)'
                    rows={2} style={{ width: '100%', marginTop: 8, padding: '6px 8px', fontSize: 13,
                      border: '1px solid #d1d5db', borderRadius: 6, boxSizing: 'border-box' }} />
                )}
              </div>
            );
          })()}

          <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
            <button onClick={() => { setStep(1); setError(''); }}
              className="btn-muted" style={{ padding: '7px 18px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
              ← Back
            </button>
            <button onClick={submit} disabled={savingReservation}
              className="btn-success" style={{ padding: '7px 22px', borderRadius: 6, fontWeight: 600, cursor: savingReservation ? 'wait' : 'pointer', fontSize: 13, opacity: savingReservation ? 0.6 : 1 }}>
              {savingReservation ? 'Saving…' : (editingId ? 'Update Reservation' : 'Submit Reservation')}
            </button>
          </div>
        </div>
      )}

      {showRecentReservations && (<>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.4rem' }}>
        <h4 style={{ margin: 0 }}>Recent Reservations</h4>
        {/* Arrivals import — Super Admin (system role 'admin') only; the API enforces it too. */}
        {String(role || '').toLowerCase() === 'admin' && <button
          type="button"
          onClick={() => setShowArrivalsImport(true)}
          style={{ padding: '0.4rem 0.8rem', border: '1px solid #0f766e', borderRadius: 6, background: '#f0fdfa', color: '#0f766e', fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer' }}
        >
          ⭱ Import Arrivals (Excel)
        </button>}
      </div>
      {showArrivalsImport && String(role || '').toLowerCase() === 'admin' && (
        <ArrivalsImportModal
          headers={headers}
          onClose={() => setShowArrivalsImport(false)}
          onImported={(d) => { setMessage(`${d.created} booking(s) imported from Excel`); loadData(); }}
        />
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'flex-end', marginBottom: '0.5rem' }}>
        <label style={{ display: 'grid', gap: 2, fontSize: '0.72rem', color: 'var(--theme-text-secondary)' }}>Date by
          <select value={resDateBasis} onChange={(e) => setResDateBasis(e.target.value)} style={{ padding: '0.3rem 0.45rem', border: '1px solid #cbd5e0', borderRadius: 5, fontSize: '0.82rem', background: '#fff' }}>
            <option value="arrival">Arrival date</option>
            <option value="booking">Booking date</option>
          </select>
        </label>
        <label style={{ display: 'grid', gap: 2, fontSize: '0.72rem', color: 'var(--theme-text-secondary)' }}>From ({resDateBasis === 'booking' ? 'booking' : 'arrival'})
          <input type="date" value={resFrom} onChange={(e) => setResFrom(e.target.value)} style={{ padding: '0.3rem 0.45rem', border: '1px solid #cbd5e0', borderRadius: 5, fontSize: '0.82rem' }} />
        </label>
        <label style={{ display: 'grid', gap: 2, fontSize: '0.72rem', color: 'var(--theme-text-secondary)' }}>To ({resDateBasis === 'booking' ? 'booking' : 'arrival'})
          <input type="date" value={resTo} onChange={(e) => setResTo(e.target.value)} style={{ padding: '0.3rem 0.45rem', border: '1px solid #cbd5e0', borderRadius: 5, fontSize: '0.82rem' }} />
        </label>
        <button onClick={() => loadReservationsPage(0)} style={{ padding: '0.35rem 0.9rem', border: 'none', background: 'var(--theme-primary)', color: '#fff', borderRadius: 5, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}>Search</button>
        {reservationFilterActive && (
          <button onClick={() => { setResFrom(''); setResTo(''); setResSearch(''); loadReservationsPage(0, { from: '', to: '', q: '' }); }} style={{ padding: '0.35rem 0.7rem', border: '1px solid #cbd5e0', background: '#fff', color: 'var(--theme-primary)', borderRadius: 5, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>Clear</button>
        )}
        <span style={{ fontSize: '0.75rem', color: 'var(--theme-text-secondary)' }}>
          {reservationFilterActive
            ? `${displayReservations.length} match${displayReservations.length === 1 ? '' : 'es'}`
            : `${resTotal} reservation${resTotal === 1 ? '' : 's'} — newest first`}
        </span>
        <label style={{ display: 'grid', gap: 2, fontSize: '0.72rem', color: 'var(--theme-text-secondary)', marginLeft: 'auto' }}>Search grid
          <input type="text" value={resSearch} onChange={(e) => setResSearch(e.target.value)} placeholder="Res No or Guest name" style={{ padding: '0.3rem 0.45rem', border: '1px solid #cbd5e0', borderRadius: 5, fontSize: '0.82rem', minWidth: 190 }} />
        </label>
      </div>
      <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 440, border: '1px solid var(--theme-border)', borderRadius: 6 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
          <thead>
            <tr style={{ color: '#fff' }}>
              <th style={{ padding: '0.45rem', textAlign: 'left', background: 'var(--theme-primary)', position: 'sticky', top: 0, zIndex: 1 }}>Res No</th>
              <th style={{ padding: '0.45rem', textAlign: 'left', background: 'var(--theme-primary)', position: 'sticky', top: 0, zIndex: 1 }}>Guest</th>
              <th style={{ padding: '0.45rem', textAlign: 'left', background: 'var(--theme-primary)', position: 'sticky', top: 0, zIndex: 1 }}>Status</th>
              <th style={{ padding: '0.45rem', textAlign: 'left', background: 'var(--theme-primary)', position: 'sticky', top: 0, zIndex: 1 }}>Arrival</th>
              <th style={{ padding: '0.45rem', textAlign: 'left', background: 'var(--theme-primary)', position: 'sticky', top: 0, zIndex: 1 }}>Departure</th>
              <th style={{ padding: '0.45rem', textAlign: 'center', background: 'var(--theme-primary)', position: 'sticky', top: 0, zIndex: 1 }}>Tot. Rooms</th>
              <th style={{ padding: '0.45rem', textAlign: 'left', background: 'var(--theme-primary)', position: 'sticky', top: 0, zIndex: 1 }}>Pax</th>
              <th style={{ padding: '0.45rem', textAlign: 'left', background: 'var(--theme-primary)', position: 'sticky', top: 0, zIndex: 1 }}>Booking Source</th>
              <th style={{ padding: '0.45rem', textAlign: 'left', background: 'var(--theme-primary)', position: 'sticky', top: 0, zIndex: 1 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {displayReservations.length === 0 && (
              <tr><td colSpan={10} style={{ padding: '1rem', textAlign: 'center', color: 'var(--theme-text-secondary)' }}>No reservations match the current filter.</td></tr>
            )}
            {displayReservations.map((r) => (
              <tr key={r.reservation_id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                <td style={{ padding: '0.45rem' }}>{r.reservation_no || `RES-${r.reservation_id}`}</td>
                <td style={{ padding: '0.45rem' }}>
                  {r.primary_guest_name}
                  {r.group_name && (
                    <span style={{ marginLeft: 6, background: '#7c3aed', color: '#fff', borderRadius: 4, padding: '1px 6px', fontSize: '0.7rem', fontWeight: 700 }}>
                      GRP: {r.group_name}
                    </span>
                  )}
                </td>
                <td style={{ padding: '0.45rem' }}>
                  {r.status}
                  {r.pre_checkin_status && r.pre_checkin_status !== 'NOT_SENT' && (
                    <span style={{
                      marginLeft: 6, borderRadius: 4, padding: '1px 5px', fontSize: '0.68rem', fontWeight: 700, color: '#fff',
                      background: r.pre_checkin_status === 'COMPLETED' ? '#059669' : r.pre_checkin_status === 'PARTIAL' ? '#d97706' : '#3b82f6'
                    }}>
                      {r.pre_checkin_status === 'COMPLETED' ? 'Pre-CI Done' : r.pre_checkin_status === 'PARTIAL' ? 'Pre-CI Partial' : 'Pre-CI Sent'}
                    </span>
                  )}
                </td>
                <td style={{ padding: '0.45rem' }}>{r.arrival_date}</td>
                <td style={{ padding: '0.45rem' }}>{r.departure_date}</td>
                <td style={{ padding: '0.45rem', textAlign: 'center', fontWeight: 600 }}>{r.room_count || '-'}</td>
                <td style={{ padding: '0.45rem' }}>{r.total_pax}</td>
                <td style={{ padding: '0.45rem' }}>
                  {r.travel_agent_name
                    ? `Travel Agent: ${r.travel_agent_name}`
                    : r.corporate_name
                      ? `Corporate: ${r.corporate_name}`
                      : r.channel_booking_id
                        ? <span>
                            <span style={{ background: '#7c3aed', color: '#fff', borderRadius: '4px', padding: '1px 5px', fontSize: '0.72rem', fontWeight: 700, marginRight: '4px' }}>OTA</span>
                            {r.booking_source}
                            {r.pay_at_hotel === false
                              ? <span style={{ marginLeft: '6px', background: 'var(--theme-success)', color: '#fff', borderRadius: '4px', padding: '1px 5px', fontSize: '0.7rem' }}>Pre-paid</span>
                              : <span style={{ marginLeft: '6px', background: 'var(--theme-warning)', color: '#fff', borderRadius: '4px', padding: '1px 5px', fontSize: '0.7rem' }}>Pay@Hotel</span>
                            }
                          </span>
                        : 'Direct'}
                  {(r.meal_plan_label || r.meal_plan_code) && (
                    <span style={{ display: 'block', marginTop: '2px', fontSize: '0.7rem', color: '#6b7280' }}>{r.meal_plan_label || r.meal_plan_code}</span>
                  )}
                </td>
                <td style={{ padding: '0.45rem', display: 'flex', gap: isMobile ? '0.3rem' : '0.4rem', flexWrap: 'wrap', flexDirection: 'row', alignItems: isMobile ? 'flex-start' : undefined, minWidth: isMobile ? 240 : undefined }}>
                  <button onClick={() => loadReservationForEdit(r.reservation_id)}>Edit</button>
                  <button onClick={() => copyReservation(r.reservation_id)}>Copy</button>
                  <button onClick={() => openVoucher(r.reservation_id)}>Voucher</button>
                  <button
                    onClick={() => openAdvanceModal(r)}
                    style={{ background: 'var(--theme-warning)', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 }}
                    title="Manage Advance / Refund"
                  >₹ Advance</button>
                  {/* REQ-20 (docs/SRS_AMBERPARAMOUNT_REQ08-15.md §15) — only show
                      this when money is ACTUALLY owed (net_advance > 0), not on
                      every cancelled reservation regardless of whether it ever
                      took a payment. Previously identical either way, which is
                      exactly how a real pending refund went unnoticed. */}
                  {String(r.status || '').toUpperCase() === 'CANCELLED' && Number(r.net_advance || 0) > 0 && (
                    <button
                      onClick={() => openAdvanceModal(r, { autoShowRefund: true })}
                      style={{ background: 'var(--theme-danger)', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.2rem 0.6rem', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, animation: 'pulse 2s infinite' }}
                      title={`Refund due: ₹${Number(r.net_advance).toFixed(2)}`}
                    >⚠ ₹{Number(r.net_advance).toFixed(2)} Refund Due</button>
                  )}
                  {(() => {
                    const st = String(r.status || '').toUpperCase();
                    const pending = st === 'CHECKED_IN' ? Number(r.pending_checkin_rooms || 0) : 0;
                    const can = ['CONFIRMED', 'GUARANTEED', 'NO_SHOW'].includes(st) || pending > 0;
                    return (
                      <button
                        onClick={() => openCiModal(r)}
                        disabled={!can}
                        title={pending > 0 ? `${pending} room${pending === 1 ? '' : 's'} of this reservation not checked in yet` : can ? 'Check In' : 'Only CONFIRMED/GUARANTEED/NO_SHOW can be checked in'}
                        style={{ opacity: can ? 1 : 0.4 }}
                      >{pending > 0 ? `Check-In (${pending} left)` : 'Check-In'}</button>
                    );
                  })()}
                  {['TENTATIVE', 'CONFIRMED', 'GUARANTEED'].includes(String(r.status || '').toUpperCase()) && (
                    <button
                      onClick={async () => {
                        const isFlagged = Boolean(r.late_arrival_flag);
                        if (isFlagged) {
                          try {
                            const res = await fetch(`/api/v1/pms/reservations/${r.reservation_id}/flag-late-arrival`, {
                              method: 'POST', headers: headers(), body: JSON.stringify({ flag: false })
                            });
                            if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error || 'Failed');
                            loadData();
                          } catch (err) { setError(err.message); }
                          return;
                        }
                        const note = window.prompt('Guest called ahead — note (e.g. "arriving ~3 AM tomorrow"):', '');
                        if (note === null) return;
                        try {
                          const res = await fetch(`/api/v1/pms/reservations/${r.reservation_id}/flag-late-arrival`, {
                            method: 'POST', headers: headers(), body: JSON.stringify({ flag: true, note })
                          });
                          if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.error || 'Failed');
                          loadData();
                        } catch (err) { setError(err.message); }
                      }}
                      style={{
                        background: r.late_arrival_flag ? '#f59e0b' : '#e2e8f0',
                        color: r.late_arrival_flag ? '#fff' : '#334155',
                        border: 'none', borderRadius: 4, padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600
                      }}
                      title={r.late_arrival_flag ? `Flagged: ${r.late_arrival_note || 'expected late arrival'} — click to clear` : 'Mark as expected late arrival (skips auto No-Show)'}
                    >{r.late_arrival_flag ? '⏰ Late Arrival' : 'Flag Late Arrival'}</button>
                  )}
                  {['TENTATIVE', 'CONFIRMED', 'GUARANTEED', 'RESERVED'].includes(String(r.status || '').toUpperCase()) && (
                    <button
                      onClick={async () => {
                        try {
                          const res = await fetch(`/api/v1/pms/pre-checkin/send/${r.reservation_id}`, {
                            method: 'POST',
                            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json',
                              'x-chain-id': selectedProperty?.chain_id || '', 'x-property-id': selectedProperty?.id || '' }
                          });
                          const data = await res.json();
                          if (!res.ok) throw new Error(data?.error || 'Failed');
                          alert('Pre-check-in link generated!\n' + (data.link || data.full_url));
                          loadData();
                        } catch (err) { alert(err.message); }
                      }}
                      style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 4, padding: '0.2rem 0.5rem', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600 }}
                      title="Send/resend pre-check-in link"
                    >Pre-CI Link</button>
                  )}
                  <select
                    value={statusDraft[r.reservation_id] || r.status}
                    onChange={(e) => setStatusDraft((prev) => ({ ...prev, [r.reservation_id]: e.target.value }))}
                  >
                    {statusOptions(r.status)}
                    <option value="CANCELLED">CANCELLED</option>
                    <option value="NO_SHOW">NO_SHOW</option>
                  </select>
                  <button onClick={() => updateStatus(r.reservation_id)}>Update Status</button>
                  <button 
                    onClick={() => openCancelModal(r)}
                    disabled={['CHECKED_IN', 'CHECKED_OUT', 'CANCELLED', 'NO_SHOW'].includes(String(r.status).toUpperCase())}
                    style={{ 
                      background: '#e53e3e', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.2rem 0.5rem', cursor: 'pointer',
                      opacity: ['CHECKED_IN', 'CHECKED_OUT', 'CANCELLED', 'NO_SHOW'].includes(String(r.status).toUpperCase()) ? 0.5 : 1
                    }}
                  >Cancel</button>
                </td>
              </tr>
            ))}
            {!rows.length ? <tr><td style={{ padding: '0.6rem' }} colSpan={10}>No reservations found</td></tr> : null}
          </tbody>
        </table>
      </div>
      {resTotal > RES_PAGE_SIZE && (() => {
        const btn = (disabled) => ({ padding: '0.3rem 0.75rem', border: '1px solid #cbd5e0', background: disabled ? '#f1f5f9' : '#fff', color: disabled ? '#94a3b8' : 'var(--theme-primary)', borderRadius: 5, cursor: disabled ? 'default' : 'pointer', fontSize: '0.8rem', fontWeight: 600 });
        const first = resPage * RES_PAGE_SIZE + 1;
        const last = Math.min(resTotal, (resPage + 1) * RES_PAGE_SIZE);
        const atStart = resPage <= 0;
        const atEnd = resPage >= resPageCount - 1;
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '0.4rem', marginTop: '0.5rem', flexWrap: 'wrap', fontSize: '0.8rem', color: 'var(--theme-text-secondary)' }}>
            <span style={{ marginRight: '0.4rem' }}>Showing {first}–{last} of {resTotal}</span>
            <button disabled={atStart} onClick={() => loadReservationsPage(0)} style={btn(atStart)}>« First</button>
            <button disabled={atStart} onClick={() => loadReservationsPage(resPage - 1)} style={btn(atStart)}>‹ Prev</button>
            <span style={{ padding: '0 0.3rem' }}>Page {resPage + 1} of {resPageCount}</span>
            <button disabled={atEnd} onClick={() => loadReservationsPage(resPage + 1)} style={btn(atEnd)}>Next ›</button>
            <button disabled={atEnd} onClick={() => loadReservationsPage(resPageCount - 1)} style={btn(atEnd)}>Last »</button>
          </div>
        );
      })()}
      </>)}

      {ciModal ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ width: ciLines.length > 1 ? 'min(720px,96vw)' : 'min(560px,96vw)', background: '#fff', borderRadius: '10px', border: '1px solid #d9e2ec', padding: '1.3rem', position: 'relative', maxHeight: '90vh', overflowY: 'auto' }}>
            <button onClick={() => setCiModal(null)} style={{ position: 'absolute', top: '0.5rem', right: '0.6rem', border: '1px solid #cbd5e0', background: '#fff', borderRadius: '4px', padding: '0.05rem 0.4rem', fontSize: '1rem', cursor: 'pointer' }} title="Close">×</button>

            {ciResult ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.8rem' }}>
                  <span style={{ fontSize: '1.4rem', color: '#166534' }}>✓</span>
                  <h4 style={{ margin: 0, color: '#166534' }}>{Array.isArray(ciResult.stays) && ciResult.stays.length > 1 ? 'Group Check-In Successful' : 'Check-In Successful'}</h4>
                </div>
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '7px', padding: '0.8rem', fontSize: '0.91rem' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.4rem', marginBottom: Array.isArray(ciResult.stays) && ciResult.stays.length > 1 ? '0.6rem' : 0 }}>
                    <div><b>Reservation:</b> {ciResult.reservation_no}</div>
                    <div><b>Guest:</b> {ciResult.primary_guest_name || ciModal.primary_guest_name || '-'}</div>
                  </div>
                  {Array.isArray(ciResult.stays) && ciResult.stays.length > 1 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ background: '#dcfce7' }}>
                          <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', borderBottom: '1px solid #bbf7d0' }}>Room</th>
                          <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', borderBottom: '1px solid #bbf7d0' }}>Stay ID</th>
                          <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', borderBottom: '1px solid #bbf7d0' }}>Folio ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ciResult.stays.map((s) => (
                          <tr key={s.stay_id} style={{ borderBottom: '1px solid #bbf7d0' }}>
                            <td style={{ padding: '0.3rem 0.5rem', fontWeight: 600 }}>{s.room_number || s.room_id}</td>
                            <td style={{ padding: '0.3rem 0.5rem' }}>{s.stay_id}</td>
                            <td style={{ padding: '0.3rem 0.5rem' }}>{s.folio_id}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.4rem' }}>
                      <div><b>Stay ID:</b> {ciResult.stay_id}</div>
                      <div><b>Folio ID:</b> {ciResult.folio_id}</div>
                    </div>
                  )}
                </div>
                {selectedProperty?.code && (() => {
                  const portalUrl = `${window.location.origin.replace(/:\d+$/, ':3001')}/?code=${encodeURIComponent(selectedProperty.code)}`;
                  return (
                    <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '7px', padding: '0.6rem 0.8rem', marginTop: '0.8rem', fontSize: '0.84rem' }}>
                      <div style={{ fontWeight: 600, color: '#1e40af', marginBottom: '0.3rem' }}>Guest Portal Link</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <input readOnly value={portalUrl} style={{ flex: 1, border: '1px solid #93c5fd', borderRadius: '4px', padding: '0.3rem 0.5rem', fontSize: '0.82rem', background: '#fff', color: '#1e3a5f' }} onClick={e => e.target.select()} />
                        <button type="button" onClick={() => { navigator.clipboard.writeText(portalUrl); }} style={{ background: '#2563eb', color: '#fff', border: 'none', borderRadius: '4px', padding: '0.3rem 0.7rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>Copy</button>
                      </div>
                      <div style={{ color: '#6b7280', fontSize: '0.75rem', marginTop: '0.25rem' }}>Share this link with the guest for room service, housekeeping & bill access</div>
                    </div>
                  );
                })()}
                <div style={{ marginTop: '1rem', textAlign: 'right' }}>
                  <button onClick={() => setCiModal(null)} className="btn-success" style={{ borderRadius: '5px', padding: '0.45rem 1.2rem', fontWeight: 600 }}>Close</button>
                </div>
              </div>
            ) : (
              <div>
                <h4 style={{ margin: '0 0 0.8rem 0', color: 'var(--theme-primary)' }}>
                  {ciLines.length > 1 ? 'Group Check-In' : 'Reservation Check-In'}
                  {ciLines.length > 1 && <span style={{ fontSize: '0.82rem', fontWeight: 400, color: 'var(--theme-text-secondary)', marginLeft: '0.5rem' }}>({ciLines.length} rooms)</span>}
                </h4>
                <div style={{ background: '#f7fafc', border: '1px solid var(--theme-border)', borderRadius: '7px', padding: '0.65rem', marginBottom: '0.9rem', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.3rem 1rem', fontSize: '0.9rem' }}>
                  <div><b>Resv No:</b> {ciModal.reservation_no || `RES-${ciModal.reservation_id}`}</div>
                  <div><b>Status:</b> <span style={{ color: '#1d4ed8', fontWeight: 600 }}>{ciModal.status}</span></div>
                  <div><b>Guest:</b> {ciModal.primary_guest_name || '-'}</div>
                  <div><b>Pax:</b> {ciModal.total_pax || '-'}</div>
                  <div><b>Arrival:</b> {ciModal.arrival_date || '-'}</div>
                  <div><b>Departure:</b> {ciModal.departure_date || '-'}</div>
                </div>

                {/* Pre-Check-In Status Banner */}
                {ciModal.pre_checkin_status && ciModal.pre_checkin_status !== 'NOT_SENT' && (
                  <div style={{
                    background: ciModal.pre_checkin_status === 'COMPLETED' ? '#f0fdf4' : ciModal.pre_checkin_status === 'PARTIAL' ? '#fffbeb' : '#eff6ff',
                    border: `1px solid ${ciModal.pre_checkin_status === 'COMPLETED' ? '#bbf7d0' : ciModal.pre_checkin_status === 'PARTIAL' ? '#fde68a' : '#bfdbfe'}`,
                    borderRadius: '7px', padding: '0.5rem 0.8rem', marginBottom: '0.9rem', fontSize: '0.84rem',
                    display: 'flex', alignItems: 'center', gap: '0.5rem'
                  }}>
                    <span style={{ fontSize: '1rem' }}>
                      {ciModal.pre_checkin_status === 'COMPLETED' ? '✓' : ciModal.pre_checkin_status === 'PARTIAL' ? '⏳' : '📧'}
                    </span>
                    <div>
                      <span style={{ fontWeight: 600, color: ciModal.pre_checkin_status === 'COMPLETED' ? '#166534' : ciModal.pre_checkin_status === 'PARTIAL' ? '#92400e' : '#1e40af' }}>
                        {ciModal.pre_checkin_status === 'COMPLETED' ? 'Pre-Check-In Complete' : ciModal.pre_checkin_status === 'PARTIAL' ? 'Pre-Check-In Partial' : 'Pre-Check-In Sent'}
                      </span>
                      <span style={{ color: '#6b7280', marginLeft: '0.4rem' }}>
                        {ciModal.pre_checkin_status === 'COMPLETED'
                          ? '— Guest details will be auto-applied on check-in'
                          : ciModal.pre_checkin_status === 'PARTIAL'
                          ? '— Some guest details submitted, rest can be filled at desk'
                          : '— Link sent, awaiting guest response'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Room Lines Table for Group Check-In */}
                {ciLines.length > 0 && (() => {
                  // Check if any room type has fewer vacant rooms than needed
                  const typeCounts = {};
                  ciLines.forEach((l) => {
                    const tid = String(l.room_type_id || '');
                    if (tid) typeCounts[tid] = (typeCounts[tid] || { needed: 0, name: l.room_type_name || '' });
                    if (tid) typeCounts[tid].needed++;
                  });
                  const shortages = Object.entries(typeCounts).map(([tid, info]) => {
                    const usedInLines = ciLines.filter((l) => l.room_id).map((l) => String(l.room_id));
                    const avail = rooms.filter((r) => r.is_active && String(r.occupancy_status || '').toUpperCase() === 'VACANT'
                      && !['BLOCKED', 'OUT_OF_ORDER', 'PAINTING'].includes(String(r.maintenance_status || '').toUpperCase())
                      && String(r.room_type_id) === tid && !usedInLines.includes(String(r.room_id))).length;
                    const assigned = ciLines.filter((l) => String(l.room_type_id) === tid && l.room_id).length;
                    const shortfall = info.needed - assigned - avail;
                    return shortfall > 0 ? `${info.name}: ${shortfall} room${shortfall > 1 ? 's' : ''} short` : null;
                  }).filter(Boolean);
                  return (
                  <>
                  {shortages.length > 0 && (
                    <div style={{ marginBottom: '0.5rem', padding: '0.4rem 0.6rem', background: '#fff7ed', border: '1px solid #fdba74',
                      borderRadius: 6, fontSize: '0.8rem', color: '#92400e', fontWeight: 600 }}>
                      Not enough vacant rooms: {shortages.join(', ')}. You can assign rooms from other types below.
                    </div>
                  )}
                  <div style={{ marginBottom: '0.8rem', border: '1px solid var(--theme-border)', borderRadius: '7px', overflow: 'hidden' }}>
                    <div style={{ background: '#eef2ff', padding: '0.4rem 0.6rem', fontWeight: 700, fontSize: '0.82rem', color: '#3730a3', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span>Room Assignments</span>
                      <button
                        type="button"
                        onClick={autoAssignRooms}
                        title="Fills empty rows with the best available room, keeping multi-room bookings on the same floor/block"
                        style={{ padding: '0.2rem 0.6rem', borderRadius: 4, border: '1px solid #6366f1', background: '#fff', color: '#4338ca', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer' }}
                      >
                        Auto-Assign Rooms
                      </button>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ background: 'var(--theme-bg-light)' }}>
                          <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', borderBottom: '1px solid var(--theme-border)' }}>Room Type</th>
                          <th style={{ padding: '0.35rem 0.5rem', textAlign: 'center', borderBottom: '1px solid var(--theme-border)' }}>Pax</th>
                          <th style={{ padding: '0.35rem 0.5rem', textAlign: 'right', borderBottom: '1px solid var(--theme-border)' }}>Rate</th>
                          <th style={{ padding: '0.35rem 0.5rem', textAlign: 'left', borderBottom: '1px solid var(--theme-border)' }}>Assign Room</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ciLines.map((line, idx) => {
                          const usedRoomIds = ciLines.filter((_, i) => i !== idx).map((l) => String(l.room_id)).filter(Boolean);
                          return (
                            <tr key={line.line_id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                              <td style={{ padding: '0.35rem 0.5rem' }}>{line.room_type_name || '-'}</td>
                              <td style={{ padding: '0.35rem 0.5rem', textAlign: 'center' }}>{line.adults}A{line.children > 0 ? ` + ${line.children}C` : ''}</td>
                              <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontFamily: 'monospace' }}>{Number(line.rate_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 })}</td>
                              <td style={{ padding: '0.35rem 0.5rem' }}>
                                <select
                                  value={line.room_id}
                                  onChange={(e) => setCiLines((prev) => prev.map((l, i) => i === idx ? { ...l, room_id: e.target.value } : l))}
                                  style={{ padding: '0.25rem 0.35rem', borderRadius: '4px', border: `1px solid ${line.room_id ? '#cbd5e0' : '#fca5a5'}`, fontSize: '0.82rem', width: '100%', background: line.room_id ? '#fff' : '#fef2f2' }}
                                >
                                  <option value="">-- Select --</option>
                                  {/* Matching room type first, then other types */}
                                  {rooms
                                    .filter((r) => r.is_active && String(r.occupancy_status || '').toUpperCase() === 'VACANT'
                                      && !['BLOCKED', 'OUT_OF_ORDER', 'PAINTING'].includes(String(r.maintenance_status || '').toUpperCase())
                                      && String(r.room_type_id) === String(line.room_type_id)
                                      && !usedRoomIds.includes(String(r.room_id)))
                                    .map((r) => (
                                      <option key={r.room_id} value={r.room_id}>
                                        {r.room_number} — {r.room_type_name || ''}
                                      </option>
                                    ))}
                                  {/* Other room types as fallback when not enough matching rooms */}
                                  {rooms
                                    .filter((r) => r.is_active && String(r.occupancy_status || '').toUpperCase() === 'VACANT'
                                      && !['BLOCKED', 'OUT_OF_ORDER', 'PAINTING'].includes(String(r.maintenance_status || '').toUpperCase())
                                      && String(r.room_type_id) !== String(line.room_type_id)
                                      && !usedRoomIds.includes(String(r.room_id)))
                                    .length > 0 && (
                                    <option disabled>── Other Room Types ──</option>
                                  )}
                                  {rooms
                                    .filter((r) => r.is_active && String(r.occupancy_status || '').toUpperCase() === 'VACANT'
                                      && !['BLOCKED', 'OUT_OF_ORDER', 'PAINTING'].includes(String(r.maintenance_status || '').toUpperCase())
                                      && String(r.room_type_id) !== String(line.room_type_id)
                                      && !usedRoomIds.includes(String(r.room_id)))
                                    .map((r) => (
                                      <option key={r.room_id} value={r.room_id}>
                                        {r.room_number} — {r.room_type_name || ''}
                                      </option>
                                    ))}
                                </select>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  </>
                  );
                })()}

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.6rem', marginBottom: '0.6rem' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                    Check-In Date &amp; Time
                    <input type="datetime-local" value={ciForm.checkin_datetime} onChange={(e) => setCiForm((f) => ({ ...f, checkin_datetime: e.target.value }))} style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                    Checkout Date &amp; Time
                    <input type="datetime-local" value={ciForm.checkout_datetime} onChange={(e) => setCiForm((f) => ({ ...f, checkout_datetime: e.target.value }))} style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                  </label>
                </div>
                {ciBookingAdvance > 0 ? (
                  <div style={{ background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', borderRadius: '5px', padding: '0.45rem 0.8rem', marginBottom: '0.6rem', fontSize: '0.82rem' }}>
                    Advance collected at booking: <b>{'₹'}{ciBookingAdvance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</b> — will be moved to the folio automatically. Enter below only any additional amount collected now.
                  </div>
                ) : null}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr 1fr', gap: '0.6rem', marginBottom: '0.7rem' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                    {ciBookingAdvance > 0 ? 'Additional Advance' : 'Advance Amount'}{ciLines.length > 1 ? <span style={{ fontWeight: 400, fontSize: '0.75rem', color: '#6b7280' }}>(first room folio)</span> : null}
                    <input type="number" min="0" step="0.01" placeholder="0.00" value={ciForm.deposit_amount} onChange={(e) => setCiForm((f) => ({ ...f, deposit_amount: e.target.value }))} style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                    Payment Mode
                    <select value={ciForm.deposit_payment_mode} onChange={(e) => setCiForm((f) => ({ ...f, deposit_payment_mode: e.target.value }))} style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }}>
                      <option value="CASH">Cash</option>
                      <option value="CARD">Card</option>
                      <option value="UPI">UPI</option>
                      <option value="BANK_TRANSFER">Bank Transfer</option>
                      <option value="CHEQUE">Cheque</option>
                      <option value="WALLET">Wallet</option>
                    </select>
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                    Reference No
                    <input type="text" placeholder="Txn / Receipt No" value={ciForm.deposit_reference_no} onChange={(e) => setCiForm((f) => ({ ...f, deposit_reference_no: e.target.value }))} style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', fontSize: '0.87rem', fontWeight: 600 }}>
                    Remarks
                    <input type="text" placeholder="Optional note" value={ciForm.deposit_remarks} onChange={(e) => setCiForm((f) => ({ ...f, deposit_remarks: e.target.value }))} style={{ padding: '0.3rem', borderRadius: '4px', border: '1px solid #cbd5e0' }} />
                  </label>
                </div>
                {ciError ? <div className="error-banner" style={{ marginBottom: '0.5rem' }}>{ciError}</div> : null}
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button onClick={() => setCiModal(null)} disabled={ciBusy}>Cancel</button>
                  <button onClick={submitCi} disabled={ciBusy} className="btn-primary" style={{ borderRadius: '5px', padding: '0.45rem 1.3rem', cursor: ciBusy ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: ciBusy ? 0.7 : 1 }}>
                    {ciBusy ? 'Processing...' : (ciLines.length > 1 ? `Check In ${ciLines.length} Rooms` : 'Confirm Check-In')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}

      {/* Cancellation Modal */}
      {cancelModal ? (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
          <div style={{ width: 'min(500px,95vw)', background: '#fff', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', borderBottom: '1px solid #eee', paddingBottom: '0.5rem' }}>
              <h3 style={{ margin: 0, color: '#e53e3e' }}>Cancel Reservation</h3>
              <button 
                onClick={() => setCancelModal(null)} 
                style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#718096' }}
              >×</button>
            </div>
            
            {cancelLoading && !cancelPreview ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>Loading Policy Details...</div>
            ) : (
              <>
                <div style={{ background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: '6px', padding: '1rem', marginBottom: '1rem' }}>
                  <div style={{ marginBottom: '0.5rem', fontWeight: 600, color: '#2d3748' }}>
                    Res #{cancelModal.reservation_no || cancelModal.reservation_id}
                  </div>
                  {cancelPreview ? (
                    <div style={{ fontSize: '0.9rem', display: 'grid', gap: '0.4rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{color: '#4a5568'}}>Policy Applied:</span>
                        <span style={{fontWeight: 500}}>{cancelPreview.policyApplied}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{color: '#4a5568'}}>Total Paid:</span>
                        <b>{cancelPreview.currency} {cancelPreview.totalPaid}</b>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#e53e3e' }}>
                        <span>Penalty:</span>
                        <b>{cancelPreview.currency} {cancelPreview.penaltyAmount}</b>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px dashed #feb2b2', paddingTop: '0.4rem', marginTop: '0.2rem' }}>
                        <span style={{fontWeight: 600}}>Refund Due:</span>
                        <b style={{color: '#2f855a'}}>{cancelPreview.currency} {cancelPreview.refundAmount}</b>
                      </div>
                      {cancelPreview.reason && <div style={{ fontSize: '0.8rem', color: '#744210', marginTop: '0.3rem', fontStyle: 'italic' }}>Note: {cancelPreview.reason}</div>}
                    </div>
                  ) : <div style={{color: '#e53e3e'}}>Failed to load preview</div>}
                </div>

                {cancelAdvanceNet > 0 && (
                  <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: 6, padding: '0.75rem 1rem', marginBottom: '1rem', fontSize: '0.87rem', color: '#92400e', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '1.1rem' }}>⚠️</span>
                    <span><b>Advance Collected: ₹{cancelAdvanceNet.toFixed(2)}</b> — Refund panel will open automatically after cancellation.</span>
                  </div>
                )}

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '0.87rem', fontWeight: 600, marginBottom: '0.3rem', color: '#4a5568' }}>Cancellation Reason</label>
                  <textarea 
                    rows={2} 
                    value={cancelForm.reason} 
                    onChange={(e) => setCancelForm(p => ({...p, reason: e.target.value}))}
                    placeholder="e.g. Guest emergency"
                    style={{ width: '100%', padding: '0.5rem', borderRadius: '4px', border: '1px solid #cbd5e0', fontSize: '0.9rem' }}
                  />
                </div>

                <div style={{ marginBottom: '1.2rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer', userSelect: 'none' }}>
                    <input 
                      type="checkbox" 
                      checked={cancelForm.overridePenalty} 
                      onChange={(e) => setCancelForm(p => ({...p, overridePenalty: e.target.checked}))}
                    />
                    <span>Override Penalty (Force Full Refund)</span>
                  </label>
                </div>

                {cancelError && <div className="error-banner" style={{ marginBottom: '1rem' }}>{cancelError}</div>}

                <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'flex-end' }}>
                  <button onClick={() => setCancelModal(null)} disabled={cancelLoading} style={{ padding: '0.5rem 1rem', background: 'var(--theme-border)', color: '#4a5568', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
                  <button 
                    onClick={submitCancellation} 
                    disabled={cancelLoading} 
                    className="btn-danger" style={{ borderRadius: '4px', padding: '0.5rem 1.2rem', cursor: cancelLoading ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: cancelLoading ? 0.7 : 1 }}
                  >
                    {cancelLoading ? 'Processing...' : 'Confirm Cancel'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      {/* ── Advance / Refund Modal ── */}
      {advanceModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div style={{ width: 'min(600px,96vw)', background: '#fff', borderRadius: 10, padding: '1.3rem', position: 'relative', maxHeight: '90vh', overflowY: 'auto' }}>
            <button onClick={() => setAdvanceModal(null)} style={{ position: 'absolute', top: '0.5rem', right: '0.6rem', border: '1px solid #cbd5e0', background: '#fff', borderRadius: 4, padding: '0.05rem 0.4rem', fontSize: '1rem', cursor: 'pointer' }}>×</button>
            <h4 style={{ margin: '0 0 0.5rem', color: String(advanceModal.status || '').toUpperCase() === 'CANCELLED' ? '#991b1b' : '#92400e' }}>
              {String(advanceModal.status || '').toUpperCase() === 'CANCELLED' ? '↩ Refund Advance' : '₹ Advance Payments'} — {advanceModal.reservation_no || `RES-${advanceModal.reservation_id}`}
            </h4>
            <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: String(advanceModal.status || '').toUpperCase() === 'CANCELLED' ? '0.5rem' : '1rem' }}>
              Guest: <b>{advanceModal.primary_guest_name}</b> &nbsp;|&nbsp; Arrival: <b>{advanceModal.arrival_date}</b>
            </div>
            {String(advanceModal.status || '').toUpperCase() === 'CANCELLED' && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '0.5rem 0.9rem', marginBottom: '1rem', fontSize: '0.85rem', color: '#991b1b', fontWeight: 600 }}>
                Reservation CANCELLED — any advance collected can be refunded below.
              </div>
            )}

            {advanceError && <div className="error-banner" style={{ marginBottom: '0.6rem' }}>{advanceError}</div>}

            {/* Transaction history */}
            {advances.length > 0 && (() => {
              const totalAdv = advances.filter(a => a.txn_type === 'ADVANCE').reduce((s, a) => s + Number(a.amount), 0);
              const totalRef = advances.filter(a => a.txn_type === 'REFUND').reduce((s, a) => s + Number(a.amount), 0);
              const net = totalAdv - totalRef;
              return (
                <div style={{ marginBottom: '1rem' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                      <tr style={{ background: 'var(--theme-bg-light)' }}>
                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Date</th>
                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Type</th>
                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right', borderBottom: '2px solid #e5e7eb' }}>Amount</th>
                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Mode</th>
                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Receipt No</th>
                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>Ref / Remarks</th>
                        <th style={{ padding: '0.4rem 0.5rem', textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {advances.map(a => (
                        <React.Fragment key={a.advance_id}>
                        <tr style={{ borderBottom: correctAdvanceRow?.advance_id === a.advance_id ? 'none' : '1px solid var(--theme-border-light)' }}>
                          <td style={{ padding: '0.35rem 0.5rem', color: '#6b7280' }}>{fmtDateMedium(a.txn_date)}</td>
                          <td style={{ padding: '0.35rem 0.5rem' }}>
                            <span style={{ background: a.txn_type === 'ADVANCE' ? '#d1fae5' : '#fee2e2', color: a.txn_type === 'ADVANCE' ? '#065f46' : '#991b1b', fontWeight: 700, fontSize: '0.72rem', padding: '0.1rem 0.45rem', borderRadius: 999 }}>{a.txn_type}</span>
                          </td>
                          <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', fontWeight: 600 }}>₹{Number(a.amount).toFixed(2)}</td>
                          <td style={{ padding: '0.35rem 0.5rem', color: 'var(--theme-text)' }}>{a.payment_mode}</td>
                          <td style={{ padding: '0.35rem 0.5rem', fontWeight: 600, color: a.receipt_no ? 'var(--theme-text)' : '#cbd5e1' }}>{a.receipt_no || '—'}</td>
                          <td style={{ padding: '0.35rem 0.5rem', color: '#6b7280' }}>{[a.reference_no, a.remarks].filter(Boolean).join(' — ')}</td>
                          <td style={{ padding: '0.35rem 0.5rem' }}>
                            <button onClick={() => (correctAdvanceRow?.advance_id === a.advance_id ? setCorrectAdvanceRow(null) : openCorrectAdvance(a))}
                              style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem', border: '1px solid #cbd5e0', background: '#fff', borderRadius: 4, cursor: 'pointer' }}>
                              {correctAdvanceRow?.advance_id === a.advance_id ? 'Cancel' : '✎ Correct'}
                            </button>
                            {canVoidAdvance(a) && (
                              <button type="button" onClick={() => voidAdvanceTxn(advanceModal.reservation_id, a)} title="Void this advance (reason required, logged)"
                                style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem', border: '1px solid #fecaca', background: '#fef2f2', color: '#b91c1c', borderRadius: 4, cursor: 'pointer', marginLeft: 4, fontWeight: 600 }}>Void</button>
                            )}
                          </td>
                        </tr>
                        {correctAdvanceRow?.advance_id === a.advance_id && (
                          <tr style={{ borderBottom: '1px solid var(--theme-border-light)' }}>
                            <td colSpan={7} style={{ padding: '0.6rem 0.5rem 0.8rem', background: '#f8fafc' }}>
                              <div style={{ fontWeight: 700, fontSize: '0.78rem', color: '#334155', marginBottom: '0.4rem' }}>Correct Payment Mode — {a.txn_type} ₹{Number(a.amount).toFixed(2)}</div>
                              {correctError && <div className="error-banner" style={{ marginBottom: '0.5rem', fontSize: '0.8rem' }}>{correctError}</div>}
                              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(150px,1fr))', gap: '0.5rem', alignItems: 'end' }}>
                                <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>New Mode
                                  <select value={correctForm.new_mode} onChange={e => setCorrectForm(f => ({ ...f, new_mode: e.target.value }))}
                                    style={{ display: 'block', width: '100%', padding: '0.3rem 0.5rem', borderRadius: 4, border: '1px solid #d1d5db', marginTop: 2 }}>
                                    {['CASH', 'CARD', 'UPI', 'BANK_TRANSFER', 'CHEQUE'].map(m => <option key={m}>{m}</option>)}
                                  </select>
                                </label>
                                <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>Reference No{['CARD', 'UPI', 'BANK_TRANSFER'].includes(correctForm.new_mode) ? ' *' : ''}
                                  <input placeholder="Txn / Cheque No" value={correctForm.reference_no}
                                    onChange={e => setCorrectForm(f => ({ ...f, reference_no: e.target.value }))}
                                    style={{ display: 'block', width: '100%', padding: '0.3rem 0.5rem', borderRadius: 4, border: '1px solid #d1d5db', marginTop: 2 }} />
                                </label>
                                <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>Reason *
                                  <input placeholder="e.g. Payment confirmed via UPI on follow-up" value={correctForm.reason}
                                    onChange={e => setCorrectForm(f => ({ ...f, reason: e.target.value }))}
                                    style={{ display: 'block', width: '100%', padding: '0.3rem 0.5rem', borderRadius: 4, border: '1px solid #d1d5db', marginTop: 2 }} />
                                </label>
                                <button onClick={submitAdvanceCorrection} disabled={correctBusy}
                                  style={{ padding: '0.4rem 0.9rem', border: 'none', background: 'var(--theme-primary)', color: '#fff', borderRadius: 6, cursor: correctBusy ? 'default' : 'pointer', fontSize: '0.8rem', fontWeight: 600, opacity: correctBusy ? 0.6 : 1 }}>
                                  {correctBusy ? 'Saving…' : 'Save Correction'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        )}
                        </React.Fragment>
                      ))}
                      {advancesVoided.map(v => (
                        <tr key={`void-${v.void_id}`} style={{ borderBottom: '1px solid var(--theme-border-light)', color: '#9ca3af' }}>
                          <td style={{ padding: '0.35rem 0.5rem', textDecoration: 'line-through' }}>{fmtDateMedium(v.txn_date)}</td>
                          <td style={{ padding: '0.35rem 0.5rem' }}>
                            <span style={{ background: '#f3f4f6', color: '#6b7280', fontWeight: 700, fontSize: '0.72rem', padding: '0.1rem 0.45rem', borderRadius: 999 }}>VOIDED</span>
                          </td>
                          <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right', textDecoration: 'line-through' }}>₹{Number(v.amount).toFixed(2)}</td>
                          <td style={{ padding: '0.35rem 0.5rem', textDecoration: 'line-through' }}>{v.payment_mode}</td>
                          <td style={{ padding: '0.35rem 0.5rem', textDecoration: 'line-through' }}>{v.receipt_no || '—'}</td>
                          <td colSpan={2} style={{ padding: '0.35rem 0.5rem' }}>Voided by {v.voided_by || '-'} — {v.void_reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', padding: '0.5rem 0.75rem', background: '#fffbeb', borderRadius: 6, fontSize: '0.875rem', flexWrap: 'wrap' }}>
                    <span>Total Advance: <b style={{ color: '#065f46' }}>₹{totalAdv.toFixed(2)}</b></span>
                    <span>Total Refund: <b style={{ color: '#991b1b' }}>₹{totalRef.toFixed(2)}</b></span>
                    <span style={{ marginLeft: 'auto' }}>Net Balance: <b style={{ color: net >= 0 ? '#1d4ed8' : 'var(--theme-danger)', fontSize: '1rem' }}>₹{net.toFixed(2)}</b></span>
                  </div>
                </div>
              );
            })()}

            {/* Add Advance form */}
            {!['CANCELLED', 'CHECKED_OUT', 'NO_SHOW'].includes(String(advanceModal.status || '').toUpperCase()) && (
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 7, padding: '0.9rem', marginBottom: '0.9rem' }}>
                <div style={{ fontWeight: 700, color: '#92400e', fontSize: '0.85rem', marginBottom: '0.6rem' }}>Record Advance Payment</div>
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(140px,1fr))', gap: '0.5rem' }}>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>Amount *
                    <input type="number" min="0" step="0.01" placeholder="0.00" value={advanceForm.amount}
                      onChange={e => setAdvanceForm(f => ({ ...f, amount: e.target.value }))}
                      style={{ display: 'block', width: '100%', padding: '0.3rem 0.5rem', borderRadius: 4, border: '1px solid #d1d5db', marginTop: 2 }} />
                  </label>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>Payment Mode
                    <select value={advanceForm.payment_mode} onChange={e => setAdvanceForm(f => ({ ...f, payment_mode: e.target.value }))}
                      style={{ display: 'block', width: '100%', padding: '0.3rem 0.5rem', borderRadius: 4, border: '1px solid #d1d5db', marginTop: 2 }}>
                      {['CASH','CARD','UPI','BANK_TRANSFER','CHEQUE'].map(m => <option key={m}>{m}</option>)}
                    </select>
                  </label>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>Reference No
                    <input placeholder="Txn / Cheque No" value={advanceForm.reference_no}
                      onChange={e => setAdvanceForm(f => ({ ...f, reference_no: e.target.value }))}
                      style={{ display: 'block', width: '100%', padding: '0.3rem 0.5rem', borderRadius: 4, border: '1px solid #d1d5db', marginTop: 2 }} />
                  </label>
                  <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>Remarks
                    <input placeholder="Optional remarks" value={advanceForm.remarks}
                      onChange={e => setAdvanceForm(f => ({ ...f, remarks: e.target.value }))}
                      style={{ display: 'block', width: '100%', padding: '0.3rem 0.5rem', borderRadius: 4, border: '1px solid #d1d5db', marginTop: 2 }} />
                  </label>
                </div>
                <button onClick={submitAdvance} disabled={advanceBusy}
                  style={{ marginTop: '0.6rem', padding: '0.4rem 1.1rem', background: 'var(--theme-warning)', color: '#fff', border: 'none', borderRadius: 5, fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem' }}>
                  {advanceBusy ? 'Saving...' : '+ Record Advance'}
                </button>
              </div>
            )}

            {/* Refund section — shown when advance exists and reservation is cancelled or manually triggered */}
            {advances.filter(a => a.txn_type === 'ADVANCE').length > 0 && (() => {
              const net = advances.filter(a => a.txn_type === 'ADVANCE').reduce((s, a) => s + Number(a.amount), 0)
                        - advances.filter(a => a.txn_type === 'REFUND').reduce((s, a) => s + Number(a.amount), 0);
              if (net <= 0) return null;
              return (
                <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, padding: '0.9rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <span style={{ fontWeight: 700, color: '#991b1b', fontSize: '0.85rem' }}>Refund (Net advance: ₹{net.toFixed(2)})</span>
                    <button onClick={() => setShowRefundPanel(p => !p)}
                      style={{ fontSize: '0.78rem', padding: '0.25rem 0.7rem', background: 'var(--theme-danger)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}>
                      {showRefundPanel ? 'Hide' : '↩ Issue Refund'}
                    </button>
                  </div>
                  {showRefundPanel && (
                    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit,minmax(140px,1fr))', gap: '0.5rem' }}>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>Refund Amount *
                        <input type="number" min="0" step="0.01" placeholder={net.toFixed(2)} value={refundForm.amount}
                          onChange={e => setRefundForm(f => ({ ...f, amount: e.target.value }))}
                          style={{ display: 'block', width: '100%', padding: '0.3rem 0.5rem', borderRadius: 4, border: '1px solid #d1d5db', marginTop: 2 }} />
                      </label>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>Payment Mode
                        <select value={refundForm.payment_mode} onChange={e => setRefundForm(f => ({ ...f, payment_mode: e.target.value }))}
                          style={{ display: 'block', width: '100%', padding: '0.3rem 0.5rem', borderRadius: 4, border: '1px solid #d1d5db', marginTop: 2 }}>
                          {['CASH','CARD','UPI','BANK_TRANSFER','CHEQUE'].map(m => <option key={m}>{m}</option>)}
                        </select>
                      </label>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>Reference No
                        <input placeholder="Refund Ref" value={refundForm.reference_no}
                          onChange={e => setRefundForm(f => ({ ...f, reference_no: e.target.value }))}
                          style={{ display: 'block', width: '100%', padding: '0.3rem 0.5rem', borderRadius: 4, border: '1px solid #d1d5db', marginTop: 2 }} />
                      </label>
                      <label style={{ fontSize: '0.78rem', fontWeight: 600 }}>Remarks
                        <input placeholder="Refund reason" value={refundForm.remarks}
                          onChange={e => setRefundForm(f => ({ ...f, remarks: e.target.value }))}
                          style={{ display: 'block', width: '100%', padding: '0.3rem 0.5rem', borderRadius: 4, border: '1px solid #d1d5db', marginTop: 2 }} />
                      </label>
                      <div style={{ alignSelf: 'flex-end' }}>
                        <button onClick={submitRefund} disabled={advanceBusy}
                          style={{ padding: '0.4rem 1.1rem', background: 'var(--theme-danger)', color: '#fff', border: 'none', borderRadius: 5, fontWeight: 700, cursor: 'pointer', fontSize: '0.85rem', width: '100%' }}>
                          {advanceBusy ? 'Saving...' : 'Confirm Refund'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            <div style={{ marginTop: '1rem', textAlign: 'right' }}>
              <button onClick={() => setAdvanceModal(null)} style={{ padding: '0.4rem 1.1rem', borderRadius: 5, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', color: 'var(--theme-text)' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Inventory Restriction Warning Modal ── */}
      {/* Pre-Check-In Prompt after reservation creation */}
      {preCheckinPrompt && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: '1.5rem 2rem', maxWidth: 460, width: '92%', boxShadow: '0 8px 30px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--theme-primary-dark)', marginBottom: '0.6rem' }}>
              Reservation Created
            </div>
            <div style={{ fontSize: '0.88rem', color: 'var(--theme-text)', marginBottom: '0.3rem' }}>
              <strong>{preCheckinPrompt.reservation_no}</strong>
              {preCheckinPrompt.guest_name ? ` — ${preCheckinPrompt.guest_name}` : ''}
            </div>
            <div style={{ fontSize: '0.85rem', color: 'var(--theme-text-secondary)', marginBottom: '1.2rem', lineHeight: 1.5 }}>
              Would you like to send a <strong>pre-check-in link</strong> to the guest? They can fill their ID details and documents before arrival, making check-in faster.
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
              <button onClick={() => setPreCheckinPrompt(null)}
                className="btn-muted" style={{ padding: '0.5rem 1rem', fontSize: '0.85rem' }}>
                Skip
              </button>
              <button
                disabled={preCheckinSending}
                onClick={async () => {
                  setPreCheckinSending(true);
                  try {
                    const res = await fetch(`/api/v1/pms/pre-checkin/send/${preCheckinPrompt.reservation_id}`, {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json',
                        'x-chain-id': selectedProperty?.chain_id || '', 'x-property-id': selectedProperty?.id || '' }
                    });
                    const data = await res.json();
                    if (!res.ok) throw new Error(data?.error || 'Failed to send');
                    setMessage(`Pre-check-in link sent for ${preCheckinPrompt.reservation_no}: ${data.short_url || data.full_url || 'Link generated'}`);
                    setPreCheckinPrompt(null);
                    loadData();
                  } catch (err) {
                    setError(err.message);
                    setPreCheckinPrompt(null);
                  } finally {
                    setPreCheckinSending(false);
                  }
                }}
                style={{ padding: '0.5rem 1.2rem', fontSize: '0.85rem', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, opacity: preCheckinSending ? 0.7 : 1 }}>
                {preCheckinSending ? 'Sending...' : 'Send Pre-Check-In Link'}
              </button>
            </div>
          </div>
        </div>
      )}

      {restrictionModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 70 }}>
          <div style={{ width: 'min(540px,94vw)', background: '#fff', borderRadius: 10, padding: '1.3rem', position: 'relative', maxHeight: '80vh', overflowY: 'auto' }}>
            <button onClick={() => setRestrictionModal(null)} style={{ position: 'absolute', top: '0.5rem', right: '0.6rem', border: '1px solid #cbd5e0', background: '#fff', borderRadius: 4, padding: '0.05rem 0.4rem', fontSize: '1rem', cursor: 'pointer' }}>×</button>
            <h4 style={{ margin: '0 0 0.6rem', color: '#b45309', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span style={{ fontSize: '1.2rem' }}>⚠</span> Inventory Restrictions
            </h4>
            <p style={{ margin: '0 0 0.7rem', fontSize: '0.85rem', color: '#6b7280' }}>
              The following restrictions are in effect for this booking:
            </p>
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '0.7rem 0.9rem', marginBottom: '1rem', maxHeight: '40vh', overflowY: 'auto' }}>
              {restrictionModal.restrictions.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'baseline', padding: '0.25rem 0', borderBottom: i < restrictionModal.restrictions.length - 1 ? '1px solid #fef3c7' : 'none', fontSize: '0.84rem' }}>
                  <span style={{ background: r.type === 'SOLD_OUT' ? '#fee2e2' : r.type === 'STOP_SELL' ? '#fecaca' : '#e0e7ff', color: r.type === 'SOLD_OUT' ? '#991b1b' : r.type === 'STOP_SELL' ? '#b91c1c' : '#3730a3', fontWeight: 700, fontSize: '0.7rem', padding: '0.1rem 0.4rem', borderRadius: 999, whiteSpace: 'nowrap' }}>{r.type}</span>
                  <span style={{ color: 'var(--theme-text)' }}>{r.message}</span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.6rem' }}>
              <button onClick={() => setRestrictionModal(null)} style={{ padding: '0.45rem 1.1rem', borderRadius: 5, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', color: 'var(--theme-text)', fontWeight: 600 }}>Cancel</button>
              {['admin', 'hotel_admin'].includes(role) && (
                <button onClick={submitWithRestrictionOverride} disabled={restrictionBusy}
                  style={{ padding: '0.45rem 1.1rem', borderRadius: 5, border: 'none', background: '#b45309', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' }}>
                  {restrictionBusy ? 'Saving...' : 'Override & Save'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
      {lockEncodeModal && (
        <LockEncodeModal
          token={token}
          selectedProperty={selectedProperty}
          reservationId={lockEncodeModal.reservationId}
          roomNumber={lockEncodeModal.roomNumber}
          guestName={lockEncodeModal.guestName}
          onClose={() => setLockEncodeModal(null)}
        />
      )}
    </div>
  );
}

export default PmsReservation;
